/*
 * Fall Sale 2026 — three-option purchase picker.
 *
 * One-time purchases only. There is deliberately no selling-plan code, no Loop price scraping and
 * no Loop widget syncing in this file; the subscription picker it descends from carried ~120 lines
 * of that, all of which would be dead weight here.
 *
 * CART MODES
 *   data-cart-mode="native"          -> single-component option. Writes the variant id into the
 *                                       theme's own buy_buttons hidden input; the native form and
 *                                       every downstream integration behave normally.
 *   data-cart-mode="fly"             -> a Fly offer backs this card, bundle (FBT-) or quantity
 *                                       break (VOL-), and the two kinds add differently:
 *                                         bundle  the theme's NATIVE submit, which Fly's request
 *                                                 interceptor rewrites in flight into its own bundle
 *                                                 payload (_armNativeFlyAdd). Fly's FBT widget
 *                                                 button is never a route for a bundle: on this
 *                                                 store it clicks the theme's native button again.
 *                                         volume  FLY'S OWN widget button, which we keep on the
 *                                                 page but hidden. That is a widget add by
 *                                                 definition, so it satisfies "Apply Discount only
 *                                                 if added from widget", and Fly runs its own
 *                                                 validation, cart-transform bookkeeping and
 *                                                 post-add behaviour.
 *                                       Skipping Fly's bookkeeping is what used to break an
 *                                       existing bundle back apart into single line items. A route
 *                                       that is not usable FAILS CLOSED: the button is disabled and
 *                                       says so, and nothing is added.
 *   data-cart-mode="bundle-pending"  -> multi-component but no offer id configured yet. The button
 *                                       is DISABLED and says so. It must never silently add one
 *                                       component of a bundle and take the customer's money for the
 *                                       wrong thing.
 *
 * There is deliberately NO fallback that adds the components itself. One existed and was removed:
 * a direct /cart/add.js carries no _flyBundles properties, and every offer has "Apply Discount only
 * if added from widget" ticked, so it charged FULL PRICE under a card advertising a discount -
 * observed against a draft offer, two loose lines at $129.98 beneath a card reading $103.98. If
 * neither Fly path is usable the button is disabled and says so. Adding nothing is a bug the
 * shopper reports; adding at the wrong price is a bug they pay for.
 *
 * ARMING (RFC 0006 section 3c, section 3-FIX1). The product form carries data-fall-armed="true" or
 * "false". The inline script data-fall-atc-guard at the top of snippets/fall-sale-picker.liquid
 * listens on WINDOW in the capture phase - ahead of every document, form and app listener - and
 * blocks every submit of that form, every click on its submit buttons (main and sticky) and every
 * click on a Fly add-to-cart button, unless the form reads "true". It also replaces the form's own
 * submit() and requestSubmit(), which fire no click: unarmed they do nothing, armed requestSubmit()
 * goes ahead and submit() is turned into requestSubmit(), so a raw native post never happens.
 * Static CSS in the same snippet hides Fly's purchase UI and the dynamic-checkout button and makes
 * the submit controls look disabled while the form is not armed. Nothing is armed until this file
 * says so, which is what closes the two windows the old code left open: the time before this
 * deferred script runs at all, and the time between a card being chosen and its Fly route actually
 * being installed - a button live in either one adds the page's own product at its normal price,
 * not the card the shopper is looking at.
 *
 * The form is armed ONLY through _setBuyDisabled(false), the one place that also enables the
 * buttons, so what the shopper sees and what the guard enforces cannot disagree. It never arms a
 * picker that is not connected. The three ways in:
 *   native card   armed at once - the theme's own form does the whole add.
 *   Fly bundle    armed by _armWhenReady once Fly has loaded, the offer is ACTIVE and inside its Fly
 *                 schedule (read from Fly's runtime list or, once Fly has trimmed that to one offer
 *                 per page, from its full list: _offerStatus), the theme-submit rewrite hook is
 *                 installed, Fly's bundle module has loaded
 *                 (window.gfbFBTModuleReady, set when its FILE loads) and Fly has installed its
 *                 add-to-cart interceptors (recorded by the guard script from the event a Fly module
 *                 emits after it has inserted its widget, which follows a network wait; a module
 *                 with no offer relevant to the page returns without emitting it), and this card has
 *                 a selection to rewrite. The hook alone proves nothing: it is inlined on every page,
 *                 and it is Fly's interceptor that calls it on an add - without that, the add goes
 *                 out plain. Nor does it say WHICH rewrite will run: an offer Fly left out of its own
 *                 bundle map is refused while the installed rewrite is Fly's stock one, which cannot
 *                 add it (_flyStockRewriteDropsOffer).
 *   Fly volume    armed by _armWhenReady once Fly has loaded, the offer is ACTIVE in Fly's data, the
 *                 widget button exists and Fly holds the tier this card means. (The widget button
 *                 posts its own payload; it does not need the interceptors.)
 * Everything else - a card switch beginning (_apply), a switched-off, out-of-schedule or missing
 * offer, Fly not arriving in ~6 seconds, a refused add, a volume card with no variant, the picker
 * leaving the page (unless another connected picker uses the same form) - leaves it "false".
 *
 * LATE RECOVERY. Fly's modules wait on the network before they finish initialising, so on a slow
 * connection the ~6 second wait above can run out first and fail a card closed: a bundle card for want
 * of the interceptors, a volume card for want of its rendered widget button or tier. A Fly module
 * emits gfb:init-req-interceptors right after it has inserted its widget (some set-up still follows
 * it, so it is not literally the last step), and only if it got that far: a module with no offer
 * relevant to the page returns before it (flyBundlesFBT.js, flyBundlesVolume.js), and one that throws
 * never reaches it. The guard script then dispatches fall-sale:fly-interceptors on document, and
 * _watchFlyInterceptors starts a fresh _armWhenReady for the selected card, bundle or volume, but only
 * if the last wait for it ran out on something Fly had not finished (an offer switched off in Fly, or
 * outside its Fly schedule, stays failed).
 *
 * ROUTING. An armed add is routed the same way whatever started it. _routeAddToCartThroughFly binds
 * one capture-phase click handler and one capture-phase submit handler, and both call
 * _routeArmedAdd: a click on the button, keyboard Enter, requestSubmit() and a dispatched submit
 * event all end up in the same place. A bundle card gets Fly's native-add state prepared BEFORE the
 * theme's own submit listener runs; a volume card cancels the submit and goes through Fly's widget
 * button; anything that cannot be routed is refused, never added plain.
 */
/* Prefix for the entries we inject into Fly's currentSelectedVariants to give one component's units
 * different variants. Fly keys that object by product handle - one slot per product - so a bundle
 * of 2 tumblers can only ever hold ONE colour. Its payload builder, however, walks the object with
 * Object.keys and only reads .id/.quantity/.isSelected/.customField from each entry, so an extra
 * key produces an extra cart line. Prefixed so ours are always distinguishable from Fly's own and
 * can be purged without touching real components. */
const UNIT_KEY = '__fallSaleUnit:';

/* Prefix for a whole selection we build ourselves when Fly has dropped an offer. Fly serves only
 * ONE bundle offer per page - deriveWidgetDataForFBTAndCheckOffersRelevantToCurrentPage ends with
 * slice(0, 1) - so the second bundle card on any product is discarded however it is configured.
 *
 * What makes recovery possible is that Santevia's own rewrite of the add - the override of
 * handleFBTAndProductBundleFormSubmit that its Fly custom script installs - does not consult the
 * discarded offer data at all. It reads state.activeOffer and state.currentSelectedVariants[offerId]
 * and nothing else, walking the selection with Object.keys and reading only
 * .id/.quantity/.isSelected/.customField - so a selection we assemble from the card's own components
 * is indistinguishable from one Fly built. Fly's STOCK handler is not like that: it needs the offer
 * in frequentBoughtDataByOfferId, so while it is the one installed a dropped offer is refused
 * instead (_flyStockRewriteDropsOffer). */
const COMPONENT_KEY = '__fallSaleComponent:';

/* Where we remember each offer's full price, so the cart drawer can show a saving.
 *
 * UpCart will not draw one for our bundles. It HAS the number - its own line data carries
 * compareAtPrice 35494 for a line it renders bare - and it renders the identical field for other
 * bundles on the same page, from the same cart. Why it treats ours differently is not determinable
 * from outside the app, and four rounds of investigation did not find it.
 *
 * So the picker supplies the display itself. sessionStorage rather than a cart property or
 * attribute: this is presentation, it must not reach the order, and it must survive the page
 * reloads a shopper makes between adding and checking out. */
const COMPARE_STORE = 'fallSaleCompareCents';

/* The largest gap, in cents, that _reconcilePrice may overwrite the Liquid price to close.
 *
 * That reconcile exists to absorb ROUNDING drift between our sum-then-round and Fly's, which is a
 * cent or two: the two documented cases were 8998 vs 8997 and 26620 vs 26621. Anything materially
 * larger is not rounding, and on 2026-08-28 a large gap turned out to mean FLY'S QUOTE WAS WRONG,
 * not ours — see D-A252 and the note on _reconcilePrice.
 *
 * 5 is comfortably above real rounding drift (worst case is roughly one cent per component) and
 * three orders of magnitude below the 1360-cent error that motivated this.
 */
const RECONCILE_MAX_DELTA_CENTS = 5;

/* What the shopper is told when something cannot be bought. Plain English, like every other message
 * this file writes; the card's own "Sold out" pill comes from the theme's locale files in Liquid. */
const SOLD_OUT_TEXT = 'This option is sold out.';
const COLOUR_SOLD_OUT_TEXT = 'That colour is sold out. Please choose another.';
const ADD_FAILED_TEXT =
  'Sorry, this could not be added to your cart. Please try another colour or a lower quantity.';
/* Shown when a Fly-backed card cannot be bought right now: its offer is switched off, outside its Fly
 * schedule or missing in Fly's data, or Fly never arrived, or its add route could not be installed
 * (a bundle that Fly's own rewrite cannot add included). */
const UNAVAILABLE_TEXT = 'This bundle is temporarily unavailable.';
/* The console reason for a dropped bundle offer that Fly's own (stock) rewrite cannot add. One text,
 * given by _bundleRouteMissing when it refuses to arm the card and by _routeArmedAdd when it fails the
 * card closed at click time. */
const STOCK_REWRITE_REFUSAL =
  "Fly's own bundle rewrite cannot add an offer it left out; Santevia's custom script is not handling the add";

/* How long a Fly-backed card waits to become buyable before it fails closed: ARM_MAX_TRIES polls,
 * ARM_POLL_MS apart - 40 x 150 ms, about 6 seconds, the same wait _whenFlyReady has always allowed. */
const ARM_POLL_MS = 150;
const ARM_MAX_TRIES = 40;

/* ---------------------------------------------------------------------------------------------
 * Watching a cart add for REJECTION.
 *
 * Before this, a rejected add was close to silent. What each path did on a 422 - the last unit
 * bought by someone else a moment ago, or the same one-left colour picked for both tumbler rows:
 *
 *   theme form (native cards, and bundles, whose payload Fly rewrites inside the THEME's request)
 *       theme.js product-form: dispatches cart:error and a 2.5s toast of responseJson.message.
 *       That message is Shopify's generic "Cart Error"; the useful sentence is in `description`,
 *       which nothing showed. A network failure throws before either, so it showed nothing.
 *   Fly widget button (quantity-break cards)
 *       Fly's own handler adds via gfb.utility.addToCart, outside the theme form, so neither the
 *       toast nor cart:error fires. Whatever Fly itself draws on failure lands in ITS widget, which
 *       this picker hides with display:none. (Inferred: Fly's module source is not in this repo and
 *       was not read. What is certain is that nothing in the theme or the picker reacted.)
 *
 * So the picker now reads the answer to the add itself and puts it under the cards, where the
 * shopper is looking. It OBSERVES, never alters: the wrapper hands back the very promise the
 * underlying fetch returned, and a request only gets looked at when it is a /cart/add made within a
 * few seconds of a click on this picker's own add button. Installed lazily on the first such click,
 * so Fly's and UpCart's own fetch wrappers are already in place underneath it and nothing about page
 * load changes. The theme's cart:error is also heard, as a backstop for a request this cannot see.
 *
 * Known gap: an add made with XMLHttpRequest rather than fetch is not observed. Fly's add helper is
 * awaited like a fetch (see _flyWidgetButton), which is why fetch is the one covered.
 * ------------------------------------------------------------------------------------------- */
const ADD_WATCHERS = new Set();

/* Where each parent gallery's own first-photo decode is tracked (RFC 0005 §3a-FIX2 item 5): keyed by
 * the gallery ELEMENT itself, at module level, rather than by card id on a picker INSTANCE. A picker
 * instance can be replaced - a theme or app partial rerender disconnects the old one and connects a
 * fresh one with the same id - but the gallery it was importing is never removed (AC-5) and neither
 * is its decode in flight, so whichever instance is current when the card is next selected can still
 * find and await the SAME promise instead of having no record of it and revealing an unfinished
 * gallery early. */
const GALLERY_LOADS = new WeakMap();

function isCartAddRequest(input) {
  let url = '';
  if (typeof input === 'string') url = input;
  else if (input && typeof input.url === 'string') url = input.url;
  else if (input && typeof input.href === 'string') url = input.href;
  return /\/cart\/add\b/.test(url);
}

function installCartAddObserver() {
  if (window.__fallSaleCartAddObserver || typeof window.fetch !== 'function') return;
  window.__fallSaleCartAddObserver = true;
  const previous = window.fetch;
  window.fetch = function fallSaleObservedFetch(input) {
    const result = previous.apply(this, arguments);
    try {
      if (ADD_WATCHERS.size && isCartAddRequest(input)) {
        ADD_WATCHERS.forEach((picker) => picker._inspectAddResponse(result));
      }
    } catch (error) {
      /* Observing must never be able to break a request. */
    }
    return result;
  };
}

class FallSalePicker extends HTMLElement {
  connectedCallback() {
    /* Every listener, observer and wait this instance adds is tied to this one controller, so
     * disconnectedCallback can take all of them away at once. Without it a picker that is replaced
     * by a theme or app rerender leaves a document-level handler behind that still routes adds for
     * a form it no longer owns, and a picker that is merely moved would end up with two of each. */
    this._abort = typeof AbortController === 'function' ? new AbortController() : { signal: undefined, abort() {} };
    const signal = this._abort.signal;

    this.form = this._findForm();
    /* Off until a card proves it is buyable. The guard script already treats a form with no marker as
     * unarmed; this makes the buttons say so as well, through the gap before the first _apply below
     * (which may wait for theme.js to define <product-gallery>). */
    this._setBuyDisabled(true);
    this.gallery = this._findGallery();
    this.pendingNote = this.querySelector('[data-fall-pending]');
    /* Error paths overwrite this note's text. Keep the original so switching options restores the
     * right message instead of showing a stale failure from a previous selection. */
    this.pendingNoteText = this.pendingNote ? this.pendingNote.textContent : '';

    /* The stepper block has usually been moved out of this element by _relocateQuantity on an earlier
     * connection, so it is looked for there too. */
    this.qtyInput =
      this.querySelector('[data-fall-qty]') ||
      (this._qtyBlock && this._qtyBlock.querySelector('[data-fall-qty]')) ||
      null;
    this._relocateQuantity(signal);

    /* Hide Fly's widgets IMMEDIATELY, not only once a Fly-backed option is chosen. Otherwise an
     * unrelated offer already active on the product renders its own tier list underneath the
     * picker - observed on the WBF page, where a year-old volume offer advertised a worse price
     * directly below ours. The picker is the only purchase surface on these templates. */
    this._hideFlyWidget();
    this._watchCartDrawer();
    this._watchAddFailures(signal);
    this._watchFlyInterceptors(signal);

    this.addEventListener('change', (event) => {
      if (!event.target) return;
      if (event.target.matches('.fall-picker__radio')) this._apply();
      else if (event.target.matches('[data-fall-colour]')) this._apply();
      else if (event.target === this.qtyInput) this._applyQuantity();
    }, { signal });

    this.addEventListener('input', (event) => {
      if (event.target === this.qtyInput) this._applyQuantity();
    }, { signal });

    this.addEventListener('click', (event) => {
      const step = event.target.closest && event.target.closest('[data-fall-qty-step]');
      if (!step) return;
      event.preventDefault();
      this._nudgeQuantity(Number(step.dataset.fallQtyStep) || 0);
    }, { signal });

    // Apply the initial selection once, after the gallery's own custom element has had a chance to
    // upgrade. Without this the very first _showCardImage can run against an un-upgraded
    // <product-gallery> and silently do nothing. scroll-carousel is defined earlier in the same
    // theme.js module, so it is ready too; _showCardImage still retries once if it is not.
    if (this.gallery && !customElements.get('product-gallery')) {
      /* The wait can outlive this connection: the picker may have been removed (or removed and
       * connected again, which runs its own first _apply) before theme.js defines the element. A
       * late _apply from the old connection would arm the form for a picker that is not there. */
      customElements.whenDefined('product-gallery').then(() => {
        if (!this.isConnected || (signal && signal.aborted)) return;
        this._apply();
      });
    } else {
      this._apply();
    }
  }

  /* Leaving the page - a theme or app partial rerender replacing this block, say - takes away every
   * listener and observer this instance added, cancels every arming wait still in flight and drops
   * the form back to unarmed, unless ANOTHER connected picker carries the same data-form-id: a
   * replacement that connected first has already armed that form for itself, and undoing it here
   * would leave the new picker looking ready while the guard blocks every add. Where no other picker
   * remains the form stays blocked rather than being left armed for a picker that no longer exists. */
  disconnectedCallback() {
    this._armGen = (this._armGen || 0) + 1;
    if (this._abort) this._abort.abort();
    /* The guards that stop a second binding are per connection, not per element. */
    this._atcRouted = false;
    this._addWatchBound = false;
    this._cartWatchBound = false;
    if (this._drawerObserver) {
      this._drawerObserver.disconnect();
      this._drawerObserver = null;
    }
    window.clearTimeout(this._drawerTimer);
    this._stopWatchingAdd();
    if (!this._sharesFormWithConnectedPicker()) this._disarmForms();
  }

  /* Is a DIFFERENT picker that is still on the page bound to the same form id as this one? */
  _sharesFormWithConnectedPicker() {
    const id = this.dataset.formId;
    if (!id) return false;
    return Array.from(document.querySelectorAll('fall-sale-picker')).some(
      (other) => other !== this && other.isConnected && other.dataset.formId === id
    );
  }

  /* Unarm the form this picker held AND whatever form with its id is in the document now - after a
   * rerender those can be two different elements, and the one in the document is the one a shopper
   * can submit. */
  _disarmForms() {
    const forms = [this.form];
    const id = this.dataset.formId;
    const current = id ? document.getElementById(id) : null;
    if (current && current !== this.form) forms.push(current);
    forms.forEach((form) => this._setFormState(form, true));
  }

  /* :not(:disabled) because a SOLD-OUT card's radio is disabled in the markup. It cannot be checked
   * by click or keyboard, but a browser restoring form state on back/forward can still set checked
   * on it - and a card nobody can buy must never be the one the add path reads. */
  get selected() {
    const checked = this.querySelector('.fall-picker__radio:checked:not(:disabled)');
    return checked ? checked.closest('.fall-picker__option') : null;
  }

  /* No purchasable card is checked. Liquid always checks the first purchasable card, so this only
   * runs after something unchecked it (the form-state restore above); re-check the first enabled
   * card rather than leave the page with no selection and a live button. */
  _selectFirstAvailableOption() {
    const radio = this.querySelector('.fall-picker__radio:not(:disabled)');
    if (!radio) return null;
    radio.checked = true;
    return radio.closest('.fall-picker__option');
  }

  /* EVERY card is sold out. The theme's own button would otherwise add the page's product at full
   * price the moment it was clicked - nothing here would be armed to intercept it. */
  _noSelectableOption() {
    this._releaseFlyState();
    this._setBuyDisabled(true);
    this._showCardGallery(null);
    if (this.pendingNote) {
      this.pendingNote.textContent = SOLD_OUT_TEXT;
      this.pendingNote.hidden = false;
    }
  }

  /* Put the stepper where the theme's own quantity selector used to sit - the price row - rather
   * than leaving it below the three cards. Quantity applies to the whole purchase, so it belongs
   * next to the price, not buried under the options.
   *
   * We move OUR control rather than re-enabling the theme's, because on the Fly products (bath
   * filter, water bottle filter) Fly hides the native selector with an inline style and
   * restore-native-quantity-selector.liquid deliberately does not restore it on a *-sale template
   * (see its own comment) - so the theme's would simply be invisible there.
   *
   * The listeners stay bound because they are delegated from <fall-sale-picker>, and the element
   * keeps its own reference to the input. Fails safe: if the row is not found the stepper stays
   * exactly where Liquid rendered it. */
  _relocateQuantity(signal) {
    /* Remembered, because once it has moved it is no longer inside this element and a later
     * connection (this element moved, not replaced) could not find it by looking here. */
    const block = this._qtyBlock || this.querySelector('.fall-picker__qty');
    if (!block) return;
    this._qtyBlock = block;
    const row = document.querySelector('.product-price-block__row');
    if (row && !row.contains(block)) {
      block.classList.add('fall-picker__qty--relocated');
      row.classList.add('product-price-block__row--with-qty');
      row.appendChild(block);
    }

    /* Delegated listeners live on <fall-sale-picker>; a stepper that sits outside it needs its own.
     * They are tied to this connection's signal, so they are bound again on every connection. */
    if (this.contains(block)) return;
    block.addEventListener('click', (event) => {
      const step = event.target.closest && event.target.closest('[data-fall-qty-step]');
      if (!step) return;
      event.preventDefault();
      this._nudgeQuantity(Number(step.dataset.fallQtyStep) || 0);
    }, { signal });
    const onInput = () => this._applyQuantity();
    block.addEventListener('input', onInput, { signal });
    block.addEventListener('change', onInput, { signal });
  }

  /* Fly has two unrelated offer systems and their ids say which: FBT- is a multi-product bundle
   * (frequentBoughtData, driven through the widget), VOL- is a quantity break (a cart-content
   * discount function). They do not share state, pricing functions, or an add path. */
  _offerKind(option) {
    const id = (option && option.dataset.flyOfferId) || '';
    if (/^VOL-/i.test(id)) return 'volume';
    if (/^FBT-/i.test(id)) return 'bundle';
    return id ? 'unknown' : 'none';
  }

  /* The chosen colour, if this option has a colour control. First unit when there are several.
   *
   * :not(:disabled) on this and _colourInputs is what keeps a sold-out or market-blocked colour out
   * of every payload: these two are the ONLY sources of the variant ids written into Fly's state or
   * the form. Liquid never marks a disabled swatch checked, so today the filter is belt-and-braces -
   * but a restored form state could, and the add path must not trust that it never will. */
  _colourInput(option) {
    return option ? option.querySelector('[data-fall-colour]:checked:not(:disabled)') : null;
  }

  /* Every chosen colour, in unit order - one per radiogroup. A single shared control gives one. */
  _colourInputs(option) {
    return option ? Array.from(option.querySelectorAll('[data-fall-colour]:checked:not(:disabled)')) : [];
  }

  /* Every colour this card COULD take - INCLUDING the sold-out and blocked ones, deliberately.
   *
   * This list is only ever used to RECOGNISE the colour component inside Fly's state (or a selection
   * rebuilt from data-components), never as the value written into it; the value always comes from
   * _colourInputs, which excludes disabled swatches. Recognition has to cover the unbuyable colours,
   * because that is exactly what Fly may be holding: its own default for the offer is set in the Fly
   * admin and knows nothing of this page's stock. When the swatch loop DROPPED sold-out colours, a
   * sold-out default in Fly's state matched nothing here, the swap returned early, and that
   * sold-out variant was the one sent to /cart/add. Now it is found and replaced by the pick.
   *
   * Matched by value rather than by position - the entry stops holding the default after the first
   * change, so matching only the default would find nothing on a second pick. */
  _colourVariantIds(option) {
    return option
      ? Array.from(option.querySelectorAll('[data-fall-colour]')).map((input) => String(input.dataset.variantId))
      : [];
  }

  /* The colour variants on this card the shopper cannot buy: sold out, or blocked for their country.
   * Liquid renders those swatches disabled, so the disabled state IS the list. */
  _unavailableColourIds(option) {
    return option
      ? Array.from(option.querySelectorAll('[data-fall-colour]:disabled')).map((input) => String(input.dataset.variantId))
      : [];
  }

  /* Does every colour row on this card have a buyable colour chosen? A card with no colour control
   * trivially does. Checked before anything is armed and again at click time: a row with no valid
   * pick would otherwise fall through to whatever default Fly or data-components happens to hold. */
  _colourPicksComplete(option) {
    if (!option) return false;
    return Array.from(option.querySelectorAll('.fall-picker__colours')).every((row) =>
      row.querySelector('[data-fall-colour]:checked:not(:disabled)')
    );
  }

  /* Give any row that has no valid pick the card's default colour - or, if that is not buyable, the
   * first colour that is. Only ever fills an EMPTY row: a colour the shopper picked is never
   * overridden, so their pick always wins over a default. On a normal render every row already has
   * its default checked by Liquid and this changes nothing. */
  _ensureColourDefaults(option) {
    if (!option) return;
    const fallbackId = String(option.dataset.colourDefault || '');
    option.querySelectorAll('.fall-picker__colours').forEach((row) => {
      if (row.querySelector('[data-fall-colour]:checked:not(:disabled)')) return;
      const buyable = Array.from(row.querySelectorAll('[data-fall-colour]:not(:disabled)'));
      const pick = buyable.find((input) => String(input.dataset.variantId) === fallbackId) || buyable[0];
      if (pick) pick.checked = true;
    });
  }

  /* Would this set of Fly entries send an unbuyable colour? The last line of defence, run on the
   * ARMED payload immediately before an add. `field` is where the variant id lives - `id` in the
   * bundle system, `variantId` in the volume one. For bundles only entries Fly will actually send
   * are counted: its payload builder skips any entry without isSelected (Fly custom script,
   * handleFBTAndProductBundleFormSubmit). The volume shape has no such flag, so every entry counts. */
  _entriesCarryUnavailable(entries, field, option, selectedOnly) {
    const unavailable = this._unavailableColourIds(option);
    if (!entries || !unavailable.length) return false;
    return Object.keys(entries).some((key) => {
      const entry = entries[key];
      if (!entry || (selectedOnly && !entry.isSelected)) return false;
      return unavailable.indexOf(String(entry[field])) !== -1;
    });
  }

  _armedBundleCarriesUnavailable(option) {
    const fbt = window.gfb && window.gfb.gfbFrequentBought;
    const byOffer = fbt && fbt.state && fbt.state.currentSelectedVariants;
    const selected = byOffer && byOffer[option.dataset.flyOfferId];
    return this._entriesCarryUnavailable(selected, 'id', option, true);
  }

  _armedVolumeCarriesUnavailable(option) {
    const vol = window.gfb && window.gfb.gfbVolumeDiscount;
    const state = vol && vol.state;
    const offerId = option.dataset.flyOfferId;
    const tier = state && state.currentSelectedTiers && state.currentSelectedTiers[offerId];
    const byTier = state && state.currentSelectedVariantsForTiers && state.currentSelectedVariantsForTiers[offerId];
    const entries = tier && byTier ? byTier['tier-' + tier.tierCount] : null;
    return this._entriesCarryUnavailable(entries, 'variantId', option, false);
  }

  /* How many units of the coloured component get their own choice. 0 or 1 means one shared choice. */
  _perUnitColourCount(option) {
    return (option && Number(option.dataset.colourPerUnit)) || 0;
  }

  /* Colour can change BOTH the variant added to cart and, if a merchant ever prices one colour
   * differently, the option's price. delta is the full-price difference before the discount, so the
   * sale price is recomputed with the option's own discount rather than assumed unchanged. */
  _effective(option) {
    /* Summed across units. A per-unit row carries the delta for ONE unit, so two rows add up to the
     * same total a single shared row would have carried at x quantity. */
    const colours = this._colourInputs(option);
    const colour = colours[0] || null;
    const delta = colours.reduce((total, input) => total + (Number(input.dataset.deltaCents) || 0), 0);
    const full = (Number(option.dataset.fullCents) || 0) + delta;
    const discount = Number(option.dataset.discount) || 0;
    /* Round ONCE on the adjusted total, matching how Liquid builds the base number and how Fly
     * actually charges. Adjusting the changed line and adding it to an already-rounded total would
     * round twice and reintroduce the cent this design exists to avoid. */
    const sale =
      delta === 0 ? Number(option.dataset.saleCents) || 0 : Math.round(full * (1 - discount));
    return {
      variantId: (colour && colour.dataset.variantId) || option.dataset.variantId,
      full,
      sale,
      changed: delta !== 0,
    };
  }

  /* Only rewrites the card when a colour actually shifts the price. Today every colour variant is
   * the same price, so this is inert - but it means a price change in admin cannot silently leave
   * the card showing a number the cart will not honour. */
  _repriceCard(option, eff) {
    if (!eff.changed) return;
    const money = (c) => this._formatMoney(c);
    const priceEl = option.querySelector('.fall-picker__price');
    const compareEl = option.querySelector('.fall-picker__compare');
    const saveEl = option.querySelector('.fall-picker__save');
    if (priceEl) priceEl.textContent = money(eff.sale);
    if (compareEl) compareEl.textContent = money(eff.full);
    if (saveEl) {
      const saved = eff.full - eff.sale;
      const pct = eff.full ? Math.round((saved / eff.full) * 100) : 0;
      saveEl.textContent = 'Save ' + money(saved) + (pct > 0 ? ' (' + pct + '%)' : '');
    }
  }

  _apply() {
    /* A picker that has left the page has nothing to apply and must not arm anything: _apply is
     * reached from waits (a gallery upgrade, a change event queued before removal) that can land after
     * disconnectedCallback has already unarmed the form. */
    if (!this.isConnected) return;

    /* A previous add's rejection belongs to the previous choice. */
    this._clearAddError();

    /* DISARM FIRST (RFC 0006 section 3c). Every pass - a card switch, or a colour change, which
     * re-runs this - starts with the form unarmed and the buttons off, and only the branch below that
     * finds this card's add path READY turns them back on. `gen` numbers the pass: an arming wait
     * started by an earlier one (a Fly poll that finishes after the shopper has moved on) sees a
     * different number and does nothing, so it can never arm the form for a card that is no longer
     * the selected one. */
    const gen = (this._armGen = (this._armGen || 0) + 1);
    this._setBuyDisabled(true);

    const option = this.selected || this._selectFirstAvailableOption();
    if (!option) {
      this._noSelectableOption();
      return;
    }

    /* Before pricing, so a row that somehow has no valid pick is priced on the colour it will get. */
    this._ensureColourDefaults(option);

    const eff = this._effective(option);
    this._repriceCard(option, eff);

    const mode = option.dataset.cartMode;
    const variantId = eff.variantId;

    /* Disarm both of Fly's submit hooks first; the branches below re-arm only what this card uses.
     * Leaving either offer active lets Fly rewrite a native add into the wrong thing entirely. */
    this._releaseFlyState();

    if (option.dataset.soldOut === 'true' || !this._colourPicksComplete(option)) {
      /* Nothing buyable to arm: a sold-out card, or a colour row with no colour in stock. Fail
       * closed BEFORE any Fly state is written, so no default can reach the cart. A sold-out card
       * cannot normally get here - its radio is disabled - so this is the guard, not the UX. */
      this._setBuyDisabled(true);
      if (this.pendingNote) {
        this.pendingNote.textContent = SOLD_OUT_TEXT;
        this.pendingNote.hidden = false;
      }
    } else if (mode === 'native' && variantId) {
      /* Nothing to wait for: the theme's own form does the whole add, so this card is ready now.
       * The one arm that is not behind a wait, so it is the one that re-checks the connection itself
       * (_setBuyDisabled refuses to arm a disconnected picker as well). */
      this._setFormVariant(variantId);
      if (this.isConnected) this._setBuyDisabled(false);
      if (this.pendingNote) this.pendingNote.hidden = true;
    } else if (mode === 'fly' && this._offerKind(option) === 'volume') {
      /* A VOLUME offer is a cart-content rule - "3 of this product gets 30%" - not a bundle. It is
       * ONE product: write the variant and force quantity to the tier size here, and the add itself
       * goes through Fly's own widget button (_routeArmedAdd -> _readyFlyWidgetButton).
       *
       * NOT a bare native form post. Every offer has "Apply Discount only if added from widget"
       * ticked, so a native add of three filters may be charged full price under a card that says
       * 30% off. That widget path is the one verified 18/18 in CA - but only because the top BUNDLE
       * card was the default, and _selectFlyOffer is where the interceptor was bound. Whenever a
       * volume card is the default instead, nothing bound it: the US/UK/intl WBF pages (top card
       * hidden) and CA once every tumbler colour is sold out. Found by the 2026-09-23 wave review.
       * Binding here is idempotent (_atcRouted), so the all-in-stock CA page is unchanged.
       *
       * It is a genuinely different system from bundles: handing a VOL- id to the FBT pricer throws
       * (observed), and gfbFrequentBought.state.activeOffer is the bundle system's state, not this
       * one. Treating them alike is what produced that error. */
      if (variantId) this._setFormVariant(variantId);
      this._routeAddToCartThroughFly();
      this._hideFlyWidget();
      /* The note is cleared BEFORE arming is attempted, not after: _armWhenReady can fail this card
       * closed on the spot (a switched-off or, for a bundle, out-of-schedule offer) and shows the
       * reason, which clearing afterwards would erase. */
      if (this.pendingNote) this.pendingNote.hidden = true;
      /* Still off from the top of this pass. It turns on only when Fly is loaded, the offer is
       * active, the widget button is on the page and Fly holds this card's tier (RFC 0006 3c) - the
       * old immediate enable let a click land before any of that was true. With no variant there is
       * nothing to add: the card fails closed WITH its note (a sold-out card was caught above; this
       * is the belt), rather than staying off with the note cleared and no reason on screen. */
      if (variantId) this._armWhenReady(option, gen, 0);
      else this._failClosed(option, UNAVAILABLE_TEXT, 'the volume card has no variant to add');
    } else if (mode === 'fly' && option.dataset.flyOfferId) {
      if (this.pendingNote) this.pendingNote.hidden = true; // before, for the same reason as above
      /* Off until _selectFlyOffer's Fly wait ends and _armWhenReady arms it. The old code enabled the
       * button right here, while the Fly route was only installed later in that async callback. */
      this._selectFlyOffer(option.dataset.flyOfferId, option, gen);
    } else {
      // No offer id configured. Fail closed: no variant is written, the button is disabled.
      this._setBuyDisabled(true);
      if (this.pendingNote) {
        this.pendingNote.textContent = this.pendingNoteText;
        this.pendingNote.hidden = false;
      }
    }

    this._showCardGallery(option);
    /* Re-derive rather than reuse `eff`. _whenFlyReady calls back SYNCHRONOUSLY once Fly is loaded,
     * so on a colour change _reconcilePrice has already corrected saleCents and written the
     * headline by the time we get here - and passing the stale `eff` would overwrite the corrected
     * number with the one it just replaced. That is exactly how the page came to show $89.98 above
     * a card reading $89.97. */
    const priced = this._effective(option);
    this._updateHeadlinePrice(priced.sale, priced.full);
    this._applyQuantity();
  }

  /* Clamp to the input's own min/max, then mirror into the form. The form has no quantity input of
   * its own on these templates - quantity-selector.liquid is suppressed on *-sale - so we create a
   * hidden one rather than assume the theme rendered it. */
  _applyQuantity() {
    if (!this.qtyInput) return;
    const min = Number(this.qtyInput.min) || 1;
    const max = Number(this.qtyInput.max) || 10;
    let value = parseInt(this.qtyInput.value, 10);
    if (!Number.isFinite(value)) value = min;
    value = Math.min(max, Math.max(min, value));
    if (String(value) !== this.qtyInput.value) this.qtyInput.value = String(value);

    /* Scope to the stepper BLOCK, not to `this`. _relocateQuantity appends that block to
     * .product-price-block__row, which is a sibling of this element and not a descendant, so
     * `this.querySelectorAll` matched NOTHING the moment the stepper moved - which is always. The
     * loop still ran, the value still clamped, and the buttons simply never disabled: minus stayed
     * lit at quantity 1 and plus at the max. _relocateQuantity already had to rebind its own click
     * listeners for exactly this reason; this query was missed at the same time.
     *
     * Deriving the scope from the live input rather than caching a node keeps it correct whether or
     * not relocation happened, and cannot go stale if the block moves again. */
    const qtyScope = this.qtyInput.closest('.fall-picker__qty') || this;
    qtyScope.querySelectorAll('[data-fall-qty-step]').forEach((btn) => {
      const dir = Number(btn.dataset.fallQtyStep) || 0;
      btn.disabled = (dir < 0 && value <= min) || (dir > 0 && value >= max);
    });

    if (!this.form) return;

    /* A volume option is N units of ONE product, so the form quantity is the tier size times the
     * stepper - pick "3-Pack" and set the stepper to 2 and you want 6 units, which is also what
     * makes Fly's quantity-break tier fire. Every other mode adds one unit per stepper step. */
    const option = this.selected;
    const units = option && this._offerKind(option) === 'volume' ? Number(option.dataset.units) || 1 : 1;
    const formQty = value * units;

    let target = this.form.querySelector('input[name="quantity"], select[name="quantity"]');
    if (!target) {
      target = document.createElement('input');
      target.type = 'hidden';
      target.name = 'quantity';
      this.form.appendChild(target);
    }
    if (target.value !== String(formQty)) target.value = String(formQty);
  }

  _nudgeQuantity(delta) {
    if (!this.qtyInput || !delta) return;
    const current = parseInt(this.qtyInput.value, 10) || 1;
    this.qtyInput.value = String(current + delta);
    this._applyQuantity();
  }

  /* ---------------------------------------------------------------------------------------------
   * FLY BUNDLES
   *
   * Fly's own widget is hidden; the picker drives it. This is the pattern already running on
   * snippets/combined-products-shower-filter-sale.liquid, not a new invention: force the active
   * offer, tick that offer's checkboxes, and route the theme's Add-to-cart through Fly's own add
   * function (Fly exposes it globally and the SF page hijacks the theme button the same way).
   *
   * Everything here is best-effort and bounded. gfb is an app global that may never arrive - the
   * app block can be missing, blocked, or slow - so nothing assumes it exists, and if it never
   * shows up the option stays unpurchasable rather than silently adding the wrong thing.
   * ------------------------------------------------------------------------------------------- */
  _whenFlyReady(callback, attempt, gen) {
    /* Superseded: a later _apply pass (another card, or this one re-applied) owns the buttons now.
     * Stop quietly - a stale wait must neither run its Fly wiring for a card that is no longer
     * selected nor time out and disable the card that IS. */
    if (gen !== undefined && gen !== this._armGen) return undefined;
    const tries = attempt || 0;
    const gfb = window.gfb;
    if (gfb && gfb.utility && gfb.gfbFrequentBought) return callback(gfb);
    if (tries >= ARM_MAX_TRIES) {
      // ~6s. Fly is not coming. Leave the option unpurchasable rather than guess.
      this._setBuyDisabled(true);
      if (this.pendingNote) {
        this.pendingNote.hidden = false;
        this.pendingNote.textContent = UNAVAILABLE_TEXT;
      }
      return undefined;
    }
    return window.setTimeout(() => this._whenFlyReady(callback, tries + 1, gen), ARM_POLL_MS);
  }

  _selectFlyOffer(offerId, option, gen) {
    this._whenFlyReady((gfb) => {
      try {
        /* Target by explicit offer id, never positionally. The SF snippet carries a comment about
         * exactly this: offers[0] was a different product's offer. */
        gfb.gfbFrequentBought.state.activeOffer = [offerId];

        document
          .querySelectorAll('.gfbProductBundleUpsellWrapper input[type="checkbox"][data-offer-id="' + offerId + '"]')
          .forEach((box) => { if (!box.checked) box.click(); });

        this._hideFlyWidget();
        this._routeAddToCartThroughFly();

        /* Apply colour BEFORE pricing, not only at click time. Fly discounts and rounds EACH LINE,
         * so splitting one 2-tumbler line into two 1-tumbler lines can move the total by a cent -
         * two different colours round twice, two identical colours round once. Reconciling against
         * the un-split state would price a bundle the shopper is not buying. */
        const byOffer = gfb.gfbFrequentBought.state.currentSelectedVariants;
        const selected = byOffer && byOffer[offerId];
        if (selected) this._setFlyColourVariant(selected, option);

        this._reconcilePrice(gfb, offerId, option);

        /* Colour used to have to reach FLY through this widget. It no longer does: _setFlyColourVariant
         * swaps the chosen variant into the payload directly, which cannot silently no-op the way
         * driving a widget control can - and on this page that control is not always rendered at
         * all. So this is now best-effort. It still matters for Fly's own price preview, which is
         * computed for whichever variant its widget holds, so a failure is still worth logging. */
        this._syncFlyVariant(option);

        /* Last, so the form is armed only once everything above ran without throwing. This is also
         * where a draft, switched-off, out-of-schedule or missing offer is caught on LOAD, not on
         * click - a disabled button with a reason beats a live button that adds the wrong thing. */
        this._armWhenReady(option, gen, 0);
      } catch (error) {
        /* If wiring Fly failed we must NOT leave a purchasable button pointing at nothing. */
        this._setBuyDisabled(true);
        if (this.pendingNote) {
          this.pendingNote.hidden = false;
          this.pendingNote.textContent = UNAVAILABLE_TEXT;
        }
        if (window.console) console.warn('[fall-sale-picker] could not select Fly offer', offerId, error);
      }
    }, 0, gen);
  }

  /* ARMING (RFC 0006 section 3c/3d) - the only place a Fly-backed card's add button is turned on.
   *
   * Polls every ARM_POLL_MS for up to ARM_MAX_TRIES and ends one of three ways:
   *   ready        _addRouteVerdict says the whole add path is in place -> arm, clear the note.
   *   unavailable  the offer is switched off, or (a bundle) outside its Fly schedule, or is a kind we
   *                do not drive -> fail closed NOW, with the reason on screen; waiting for Fly
   *                cannot change any of those.
   *   waiting      something has not arrived yet (Fly, the offer list, the rewrite hook, Fly's bundle
   *                module and its request interceptors, the widget button, the tier) -> try again;
   *                when the tries run out, fail closed, and remember (_armGaveUp) that it was a wait
   *                that ran out, which is what lets _watchFlyInterceptors start one more wait if
   *                what it was waiting for (the interceptors; for a volume card, its widget) turns
   *                up late.
   *
   * Stale-proof: it stops silently if a later _apply pass has started (`gen`), the shopper has
   * chosen another card, or the picker has left the page. When Fly is already loaded the first pass
   * runs synchronously, so a ready card is armed inside the same _apply that selected it.
   *
   * Whatever it was still waiting for when it gave up goes to the console with the fail-closed line,
   * so a card that stays off with offers switched on can be diagnosed from the page alone. */
  _armWhenReady(option, gen, attempt) {
    if (gen !== this._armGen || this.selected !== option || !this.isConnected) return;
    /* A wait is starting, so nothing has given up any more; set again below if this one does. */
    if (!attempt) this._armGaveUp = undefined;

    const verdict = this._addRouteVerdict(option);
    if (verdict.state === 'ready') {
      if (this.pendingNote) this.pendingNote.hidden = true;
      this._setBuyDisabled(false);
      return;
    }
    const tries = attempt || 0;
    if (verdict.state === 'unavailable' || tries >= ARM_MAX_TRIES) {
      /* Only a wait that ran out on something still to arrive can be retried later. A switched-off
       * (or, for a bundle, out-of-schedule) offer is 'unavailable': a wait for Fly, or a late event
       * from it, cannot change that. */
      this._armGaveUp = verdict.state === 'waiting' ? gen : undefined;
      this._failClosed(option, UNAVAILABLE_TEXT, verdict.why);
      return;
    }
    window.setTimeout(() => this._armWhenReady(option, gen, tries + 1), ARM_POLL_MS);
  }

  /* LATE RECOVERY (RFC 0006 section 3-FIX2, section 3-FIX4). Fly's modules wait on the network before
   * they finish, so on a slow connection the ~6 second wait in _armWhenReady can fail a Fly-backed card
   * closed before what it needs has arrived: a bundle card with "Fly has not installed its add-to-cart
   * interceptors yet", a volume card with "Fly has not rendered its widget button for this offer" (or
   * its tier). A Fly module emits gfb:init-req-interceptors right after it has inserted its widget
   * (the volume module: right after it has rendered it), which is not literally the last step - some
   * set-up follows - and only if it got that far: a module with no offer relevant to the page returns
   * first and emits nothing. The guard script announces the event as fall-sale:fly-interceptors on
   * document (from a zero-delay timeout, so every listener of Fly's own dispatch has run first); this
   * answers with a fresh wait for the selected card, bundle or volume, under the same rule for both.
   *
   * It acts only when the wait of the CURRENT pass gave up on something still to arrive (_armGaveUp,
   * cleared whenever a wait starts), which is what keeps it safe:
   *   - a wait still polling is left alone and sees the flag itself, so a card never has two chains;
   *   - a card that failed for another reason stays failed: a switched-off or out-of-schedule offer, a
   *     sold-out card or colour, a refused add. Re-arming those from here would skip the checks
   *     _apply makes;
   *   - an event that arrives after the shopper has moved on, or after the picker has left the page,
   *     does nothing.
   * The guards _armWhenReady applies to any wait (pass, selected card, connected) still hold. */
  _watchFlyInterceptors(signal) {
    document.addEventListener(
      'fall-sale:fly-interceptors',
      () => {
        const gen = this._armGen;
        const option = this.selected;
        if (!this.isConnected || !this.form || !gen || !option) return;
        const kind = this._offerKind(option);
        if (kind !== 'bundle' && kind !== 'volume') return;
        if (this.form.getAttribute('data-fall-armed') === 'true') return;
        if (this._armGaveUp !== gen) return;
        this._armWhenReady(option, gen, 0);
      },
      { signal }
    );
  }

  /* Can this card be bought through Fly right now? { state, why }: state is 'ready', 'waiting' or
   * 'unavailable'; why names the first thing that is not in place (empty when ready).
   *
   * Common to both kinds: Fly is loaded, the offer is in Fly's data AND active (_offerStatus), and
   * this picker's own add route is bound (_atcRouted - without it a click or a submit would go to the
   * theme's plain form). Then per kind, exactly what _routeArmedAdd will need in order not to fail
   * closed itself - so a card is armed only if its add will actually go through Fly:
   *   bundle  the theme-submit rewrite hook is installed, Fly's bundle module has loaded AND Fly has
   *           installed its request interceptors (_flyInterceptorGap: the hook is Santevia's own
   *           script and is on every page; it is Fly's interceptor that calls it on an add, and
   *           only once that is installed does a native add get rewritten instead of going out
   *           plain), AND the rewrite that will run can add this card (_flyStockRewriteDropsOffer:
   *           Fly's stock rewrite cannot add an offer Fly left out of its bundle map), AND there is
   *           a selection to rewrite: Fly's own, or one _adoptDroppedOffer can build from
   *           data-components (Fly keeps ONE bundle offer per page, so a second bundle card has none
   *           of its own).
   *   volume  Fly's volume module has state, the hidden widget button for the offer is on the page,
   *           and Fly holds the tier this card's unit count means (_volumeTierReady).
   * An offer that is merely absent from the lists Fly has loaded (for a bundle, its runtime list, its
   * loaded config and, when that is not loaded, the inline data; for volume, its runtime list - all as
   * _offerStatus reads them) is 'waiting', not 'unavailable': Fly fills its lists asynchronously, so
   * absence only means something after the wait. One that is present with isActive false - or, for a
   * bundle, outside its Fly schedule, or with a schedule too malformed to read - is 'unavailable' at
   * once. The module and the interceptors are 'waiting' too, never 'unavailable': they arrive on their
   * own, however late. So is a dropped bundle offer behind Fly's stock rewrite, though nothing arrives
   * to change that: the wait runs out and the card fails closed. */
  _addRouteVerdict(option) {
    const offerId = option && option.dataset.flyOfferId;
    const kind = this._offerKind(option);
    const done = (state, why) => ({ state: state, why: why || '' });
    if (!offerId || (kind !== 'bundle' && kind !== 'volume')) {
      return done('unavailable', 'no FBT- or VOL- offer id on the card');
    }

    const gfb = window.gfb;
    if (!gfb || !gfb.utility || !gfb.settings) return done('waiting', 'Fly (window.gfb) has not loaded');

    const status = this._offerStatus(gfb, offerId, kind);
    if (status === 'inactive') return done('unavailable', 'the offer is switched off in Fly (isActive is not true), or a bundle offer is outside its Fly schedule');
    if (status !== 'live') {
      return done(
        'waiting',
        kind === 'volume'
          ? 'the offer is not in the runtime volume list Fly is acting on (Fly keeps ONE volume offer per page, ' +
              "so an active offer that lost that slot reads the same), or Fly's lists have not loaded yet"
          : 'the offer is absent from the lists Fly has loaded - its runtime list, its loaded config, or the ' +
              "page's inline data when that config is not loaded - or they have not loaded yet"
      );
    }
    if (!this._atcRouted) return done('waiting', "the picker's add-to-cart route is not bound yet");

    const missing = kind === 'volume' ? this._volumeRouteMissing(gfb, option) : this._bundleRouteMissing(gfb, option);
    return missing ? done('waiting', missing) : done('ready');
  }

  /* What a bundle card still lacks before its add can go through Fly, or '' if nothing. A bundle is a
   * native theme submit that Fly rewrites in flight, so besides its state and the rewrite hook this
   * needs the thing that runs the rewrite (_flyInterceptorGap): the hook being present is not proof
   * that anything will call it. And for an offer Fly left out of its bundle map it needs a rewrite that
   * can add it (_flyStockRewriteDropsOffer): the hook being present does not say which one it is. */
  _bundleRouteMissing(gfb, option) {
    const fbt = gfb.gfbFrequentBought;
    if (!fbt || !fbt.state) return 'the bundle module (gfbFrequentBought) has no state';
    if (typeof gfb.utility.handleFBTAndProductBundleFormSubmit !== 'function') {
      return 'the theme-submit rewrite hook (handleFBTAndProductBundleFormSubmit) is not installed';
    }
    const gap = this._flyInterceptorGap();
    if (gap) return gap;
    if (this._flyStockRewriteDropsOffer(gfb, option.dataset.flyOfferId)) return STOCK_REWRITE_REFUSAL;
    const own = fbt.state.currentSelectedVariants && fbt.state.currentSelectedVariants[option.dataset.flyOfferId];
    if ((own && Object.keys(own).length) || this._selectionFromComponents(option)) return '';
    return 'no selection to rewrite: Fly has none and the card has no data-components';
  }

  /* What of Fly's add-to-cart request interceptors is still missing, or '' when both facts hold. They
   * become true at very different times, so both are needed (RFC 0006 section 3-FIX2):
   *   window.gfbFBTModuleReady   set when Fly's bundle module FILE loads, long before its init runs.
   *   flyInterceptors            set by the guard script (snippets/fall-sale-picker.liquid) when Fly
   *                              dispatches gfb:init-req-interceptors, which a module does once it
   *                              has inserted its widget, so after a network wait (and never, if it
   *                              found no offer relevant to the page). Fly's core installs its
   *                              fetch, jQuery ajax and XHR wrappers in a listener for that event.
   * Only those wrappers run the payload rewrite on an add, and only while the module flag is set; with
   * either missing a native add goes out as a plain, undiscounted one. Asked when arming
   * (_bundleRouteMissing) and again at click or submit time (_armNativeFlyAdd), from this one place so
   * the two cannot disagree. A guard script that is missing reads as "not installed": fails closed. */
  _flyInterceptorGap() {
    if (window.gfbFBTModuleReady !== true) return "Fly's bundle module has not loaded on this page";
    const guard = window.__fallAtcGuard;
    if (!guard || guard.flyInterceptors !== true) return 'Fly has not installed its add-to-cart interceptors yet';
    return '';
  }

  /* Would Fly's OWN bundle rewrite fail to add this offer? True only when BOTH hold (RFC 0006 section
   * 3-FIX5):
   *   (a) the offer is not a key of gfb.gfbFrequentBought.state.frequentBoughtDataByOfferId. Fly builds
   *       that map from the list it trimmed to one offer per page, so an offer it left out has no
   *       entry; a missing or non-object map counts as "not a key".
   *   (b) the rewrite installed at gfb.utility.handleFBTAndProductBundleFormSubmit is identifiably
   *       Fly's stock one: gfb.gfbFrequentBought.utility has that function and it is the very same
   *       function. Fly's bundle module copies its own onto gfb.utility when its FILE loads, and only
   *       if nothing is there yet; Santevia's Fly custom script is eval'd later by Fly's core
   *       (setCustomScript, once the module files have loaded) and OVERWRITES that copy. So the two
   *       differ because the override replaced Fly's copy. (Were the custom script ever to run first,
   *       the module would leave it alone and they would differ just the same: either order ends with
   *       them different, and only a custom script that is absent, disabled or leaves this handler alone
   *       leaves them equal.)
   * Why it matters: Fly's stock prepareCartPayloadForProductBundle reads
   * frequentBoughtDataByOfferId[offerId].ctType, which throws for a left-out offer; Fly's safeAsync
   * wrapper swallows that into an empty item list, and the stock handler then returns the form data
   * UNCHANGED, so the "bundle" goes into the cart as a PLAIN add at full price. Santevia's rewrite reads
   * only activeOffer and currentSelectedVariants, which is the only reason a left-out second bundle can
   * be added at all. Any other case is false: Fly kept the offer, or the installed rewrite is not Fly's
   * stock function.
   *
   * It compares the two functions and never calls either (a probe call would run Fly's code against
   * live state). Used by _bundleRouteMissing (arming), _armNativeFlyAdd (the click or submit) and
   * _adoptDroppedOffer, so arming, the add and adopting cannot disagree. It never throws; if reading
   * Fly's objects throws, the answer is true (refuse): this guard exists to fail closed. */
  _flyStockRewriteDropsOffer(gfb, offerId) {
    try {
      const fbt = gfb && gfb.gfbFrequentBought;
      const byOffer = fbt && fbt.state && fbt.state.frequentBoughtDataByOfferId;
      const kept =
        !!byOffer && typeof byOffer === 'object' && Object.prototype.hasOwnProperty.call(byOffer, offerId);
      if (kept) return false;
      const stock = fbt && fbt.utility && fbt.utility.handleFBTAndProductBundleFormSubmit;
      const installed = gfb && gfb.utility && gfb.utility.handleFBTAndProductBundleFormSubmit;
      return typeof stock === 'function' && stock === installed;
    } catch (error) {
      return true;
    }
  }

  /* What a volume card still lacks before its add can go through Fly's widget button, or ''. */
  _volumeRouteMissing(gfb, option) {
    const vol = gfb.gfbVolumeDiscount;
    if (!vol || !vol.state) return 'the volume module (gfbVolumeDiscount) has no state';
    const units = Number(option.dataset.units) || 0;
    if (!units) return 'the card has no unit count';
    if (!this._flyWidgetButton(option, true)) return 'Fly has not rendered its widget button for this offer';
    if (!this._volumeTierReady(vol.state, option.dataset.flyOfferId, units)) {
      return 'Fly does not hold a ' + units + '-unit tier for this offer';
    }
    return '';
  }

  /* Drive Fly's own variant radio for a coloured component.
   *
   * Fly renders a variant chooser for any component with more than one selected variant, into
   * .gfbCustomVariantContainer as .variant-input > input[type="radio"]. We hide that UI and drive
   * it, exactly as we drive Fly's checkboxes.
   *
   * HOW THE RADIO CARRIES ITS VARIANT ID IS NOT DOCUMENTED and no existing snippet in this repo
   * selects one by id - the two that touch .variant-input only distinguish .bundle-label from
   * :not(.bundle-label), which is a different problem. So this tries the plausible carriers rather
   * than betting on one, and if none matches it returns FALSE so the caller disables the button.
   *
   * Returns true when there is nothing to do (no colour control, or one already correct) or when
   * the right radio was found and clicked. Returns false ONLY when a colour is chosen and Fly's
   * matching radio could not be identified - the case where proceeding would add the wrong colour.
   */
  _syncFlyVariant(option) {
    const colour = this._colourInput(option);
    if (!colour) return true; // no colour control on this option

    const wanted = String(colour.dataset.variantId || '');
    if (!wanted) return true;

    const radios = Array.from(
      document.querySelectorAll('.gfbCustomVariantContainer input[type="radio"], .gfbCustomVariantContainer select')
    );
    /* No chooser rendered. This is the NORMAL case on these templates - the offer's variant UI is
     * not built into .gfbCustomVariantContainer - and it is no longer a problem: colour reaches the
     * cart through _setFlyColourVariant, which writes Fly's variant state directly. Silent on
     * purpose; it used to warn on every single render about something already handled. */
    if (!radios.length) return false;

    const matches = (el) =>
      String(el.value || '') === wanted ||
      String(el.dataset.variantId || '') === wanted ||
      String(el.dataset.value || '') === wanted ||
      String(el.getAttribute('data-variant') || '') === wanted;

    const target = radios.find(matches);
    if (!target) {
      if (window.console) {
        console.warn(
          '[fall-sale-picker] could not match Fly variant control to variant ' + wanted +
            '. Fly rendered these instead:',
          radios.map((el) => ({
            tag: el.tagName,
            value: el.value,
            dataset: Object.assign({}, el.dataset),
          }))
        );
      }
      return false; // fail closed - never let Fly add a colour the shopper did not pick
    }

    if (target.tagName === 'SELECT') {
      if (target.value !== wanted) {
        target.value = wanted;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (!target.checked) {
      target.click();
    }
    return true;
  }

  /* The selector list below is ALSO written out, word for word, in the static style block at the top
   * of snippets/fall-sale-picker.liquid (RFC 0006 section 3-FIX1 item 3b), which is what hides Fly's
   * purchase UI in the time before this file loads. This injection stays as the fallback, and the two
   * lists must be changed together. */
  _hideFlyWidget() {
    if (document.getElementById('fall-picker-fly-hide')) return;
    const style = document.createElement('style');
    style.id = 'fall-picker-fly-hide';
    /* Hide by BLOCK ID first - the theme wraps every block in
     * .product-info__block-item[data-block-id], so this is exact and does not depend on knowing
     * Fly's internal class names, which differ between the bundle widget and the volume widget.
     * The class rules stay as a second line of defence for anything Fly renders OUTSIDE its block
     * wrapper, such as the footer add-to-cart bar. */
    /* Fly does NOT necessarily render through the app blocks we place. Its theme app EMBED
     * auto-injects widgets wherever it decides, which is why a probe found every
     * [data-block-id="fly_bundles_*"] absent while the volume widget was plainly on the page.
     *
     * So match on Fly's own class prefixes with [class*=], not on our block ids and not on exact
     * class names - the volume widget is .gfbVolumeDiscountWidget..., the bundle widget is
     * .gfbProductBundleUpsell..., and Fly appends template suffixes to both. The block-id rules
     * stay for the case where it does render in-block. */
    style.textContent =
      '[data-block-id="fly_bundles_app_block"],[data-block-id="fly_bundles_volume_block"],' +
      '[data-block-id="fly_bundles_fbt_block"],' +
      '[class*="gfbVolumeDiscountWidget"],[class*="gfbProductBundleUpsell"],' +
      '[class*="gfbFrequentBought"],[class*="gfbFooterAddToCart"],' +
      '.gfbCustomVariantContainer{display:none !important}';
    document.head.appendChild(style);
  }

  /* A multi-component bundle is added through FLY's own add path, so the line items carry whatever
   * properties Fly attaches and "Apply Discount only if added from widget" can stay ticked.
   *
   * Bound once per connection, in the capture phase. Two handlers - one for clicks, one for submits -
   * both hand the event to _routeArmedAdd, which decides from the current selection, so the native
   * single-product path is left completely alone.
   *
   * WHY A SUBMIT HANDLER TOO. Routing used to live only in the click handler. A submit that is not a
   * click on the button - form.requestSubmit(), a dispatched submit event - never reached it, so it
   * went straight to the theme's own submit listener with Fly's native-add state unset: a bundle
   * card added the page's plain product at its normal price, and a volume card added it without its
   * tier. (form.submit() fires no submit event at all; the guard script turns that into
   * requestSubmit(), so it arrives here as a submit like the rest.) */
  _routeAddToCartThroughFly() {
    if (this._atcRouted || !this.form) return;
    this._atcRouted = true;
    const signal = this._abort && this._abort.signal;

    /* Bound on DOCUMENT, not on the form.
     *
     * This theme's sticky add-to-cart sits OUTSIDE the form and is tied to it by a `form=`
     * attribute, so its clicks never pass through the form element and a listener there simply
     * never sees them. The result was an unintercepted native submit adding the page's own single
     * variant - which is exactly what "it just adds the pitcher" was.
     *
     * The same trap already bit _setBuyDisabled, which had to move from querySelectorAll to
     * form.elements for the same reason. `btn.form` is the matching fix here: it resolves the
     * association however the button is attached, so an outside button is claimed and another
     * form's button is ignored. */
    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest && event.target.closest('[type="submit"], button[name="add"]');
        if (!btn) return;
        if (btn.form !== this.form) return; // a submit button belonging to some other form
        this._routeArmedAdd(event);
      },
      { capture: true, signal }
    );

    /* The submit itself, on DOCUMENT in the capture phase. Not for the click handler's reason - a
     * submit is always dispatched on the form itself - but because a document capture listener runs
     * before ANY listener on the form, including UpCart's own capture listener there, which was
     * registered long before this one and would otherwise get the submit first. The order is real,
     * not assumed (read from assets/theme.js and from the listeners registered on a live page): the
     * guard's window capture listener runs first, then this one, then UpCart's capture listener on
     * the form itself (upcart-bundle.js, which takes the submit and sends the add), then the theme's
     * own product-form listener on the form in the bubble phase (theme.js, ProductForm). So a bundle
     * card's Fly state is prepared before either of them reads the form and Fly rewrites the request.
     * Another app's own document-level capture listener runs in registration order relative to this
     * one; the only one seen on a live page registered after it. */
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target !== this.form) return; // another form's submit
        this._routeArmedAdd(event);
      },
      { capture: true, signal }
    );
  }

  /* One add attempt on this picker's form that the guard let through - a click on its submit button
   * (main or sticky), or a submit of the form itself: keyboard Enter, requestSubmit(), a dispatched
   * submit event, or form.submit() after the guard turned it into requestSubmit(). Decides which path
   * the add takes, and stops the event itself whenever it diverts or refuses it.
   *
   *   sold out / no colour   refused
   *   native card            left alone - the theme's own submit does the whole add
   *   bundle card            Fly's native-add state prepared, the event left to go on to the theme
   *   bundle card, no route  refused, the card fails closed - never Fly's widget button (see below)
   *   volume card            event cancelled, Fly's widget button clicked instead
   *   volume card, no route  refused, the card fails closed
   *
   * A click on a bundle card is routed here and then, if it goes on, routed again by the submit it
   * causes. That second pass sets the same state again and is harmless.
   *
   * A BUNDLE NEVER FALLS BACK TO FLY'S WIDGET BUTTON. Fly's bundle (FBT) button does not post a payload
   * of its own on this store: merchantInfo.isNativeATCEnabledForFBTAndPB is on, so its handler sets
   * addBundleUsingNativeATC and clicks the THEME's native add button, which lands back in this method.
   * With the native route refused here it is refused again there, the widget is clicked again, and the
   * form is left armed in a loop instead of failing closed. Only Fly's volume widget builds and posts
   * its own payload, so only a volume card may be routed to its widget button. */
  _routeArmedAdd(event) {
    /* A refused or diverted submit must reach nobody: not the theme's handler and not another
     * listener at this level. A click keeps the plain stopPropagation it has always had. */
    const stop = () => {
      event.preventDefault();
      if (event.type === 'submit') event.stopImmediatePropagation();
      else event.stopPropagation();
    };

    const option = this.selected;
    /* The guard script has already turned away every add while the form is not armed, and only
     * armed adds get this far. It is a separate script, though: if it is missing, or its listener
     * was lost, an add on a form this picker has not armed must still not go through. And with no
     * card selected there is nothing to route, which fails closed as well. */
    if (!option || this.form.getAttribute('data-fall-armed') !== 'true') {
      stop();
      this._failClosed(option, UNAVAILABLE_TEXT, option ? 'the form is not armed' : 'no card is selected');
      return;
    }

    /* Re-assert quantity at CLICK time, not only on selection. Fly's volume module pushes the
     * active tier's quantity into the theme's own input - updateThemeQuantity(conditionValue) -
     * and it does not put it back when the shopper moves to another card. That is how the
     * single-filter card ended up adding three. Whoever wrote last, we write last. */
    this._applyQuantity();

    /* A card with a colour row that has no buyable colour chosen never reaches either add path.
     * Ahead of the native return on purpose, so it covers every mode, not just Fly's. */
    if (option.dataset.soldOut === 'true' || !this._colourPicksComplete(option)) {
      stop();
      this._blockAdd(SOLD_OUT_TEXT);
      return;
    }

    if (option.dataset.cartMode !== 'fly') return; // native path: leave it alone
    /* Bundles go back through the theme's own submit and let Fly rewrite the payload in flight.
     * That path is proven on this offer; the widget button is not. */
    this._rememberCompare(option);
    if (this._armNativeFlyAdd(option)) {
      /* Checked on the payload Fly is ABOUT to send, after every rewrite of it - Fly's own
       * selection, or one rebuilt from data-components, with the shopper's colours swapped in.
       * If a sold-out colour survived all of that, stop here rather than let Fly send it. */
      if (this._armedBundleCarriesUnavailable(option)) {
        const fbt = window.gfb && window.gfb.gfbFrequentBought;
        if (fbt && fbt.state) fbt.state.addBundleUsingNativeATC = false;
        stop();
        this._blockAdd(COLOUR_SOLD_OUT_TEXT);
      }
      return;
    }

    /* The native route could not be prepared. For a bundle that is the end of it - Fly's widget button
     * is not a fallback for one (see the note on this method) - so refuse and fail closed. The event is
     * stopped, Fly's native-add flag is cleared so no half-armed native add can survive, and the card
     * drops to unarmed with its note. The reason named is the missing interceptor when that is what
     * refused it, else the stock-rewrite refusal for an offer Fly's own rewrite cannot add. */
    if (this._offerKind(option) !== 'volume') {
      stop();
      const fbt = window.gfb && window.gfb.gfbFrequentBought;
      if (fbt && fbt.state) fbt.state.addBundleUsingNativeATC = false;
      this._failClosed(
        option,
        UNAVAILABLE_TEXT,
        this._flyInterceptorGap() ||
          (this._flyStockRewriteDropsOffer(window.gfb, option.dataset.flyOfferId) ? STOCK_REWRITE_REFUSAL : '') ||
          "Fly's native bundle add could not be prepared"
      );
      return;
    }
    const widgetButton = this._readyFlyWidgetButton(option);
    if (widgetButton) {
      stop();
      if (this._armedVolumeCarriesUnavailable(option)) {
        this._blockAdd(COLOUR_SOLD_OUT_TEXT);
        return;
      }
      /* The form is armed (checked above), so the guard lets this click on Fly's own button through. */
      widgetButton.click();
      return;
    }

    /* A volume card with no usable widget route: FAIL CLOSED rather than add the components ourselves.
     * (A bundle never gets here: it either went on through the native route or failed closed above.)
     *
     * That fallback used to run here and it is not safe with the current settings. A direct
     * /cart/add.js carries no _flyBundles properties, so a bundle offer's discount does not
     * apply, and every offer has "Apply Discount only if added from widget" ticked, so a volume
     * offer's does not either. The shopper would be charged full price for a card advertising
     * a discount - observed on a DRAFT offer, which added two loose lines at $129.98 under a
     * card reading $103.98.
     *
     * Adding nothing is a bug the shopper reports. Adding at the wrong price is a bug they pay
     * for and we discover in a support ticket. */
    stop();
    this._failClosed(option, UNAVAILABLE_TEXT);
  }

  /* BUNDLES: hand the add back to the theme and let Fly rewrite the payload in flight.
   *
   * This was removed a commit ago in favour of clicking Fly's widget button everywhere, and that
   * was a mistake - it took a path proven working on this offer and replaced it with one that had
   * never been tried on it. The bundle button then did nothing at all. Restored, and now scoped to
   * bundles only, which is where it works: the FBT handler here is the CUSTOM SCRIPT override, and
   * unlike Fly's stock volume handler it has no isNativeATCEnabled gate to satisfy.
   *
   *     if (activeOffers.length == 0 || !addBundleUsingNativeATC) return null;
   *
   * Volume keeps the widget button, which is proven on its side. Each card uses the path that
   * actually works for it rather than one shape imposed on both. */
  _armNativeFlyAdd(option) {
    if (this._offerKind(option) !== 'bundle') return false;
    const gfb = window.gfb;
    /* The rewrite must be in place, or the native submit quietly adds the page's own single variant
     * at the wrong price - a silent overcharge, the one outcome worth failing closed for. That takes
     * the hook AND Fly's request interceptor, which is what calls it: the hook is Santevia's own
     * script, inlined on every page, so its presence proves nothing about the add being rewritten.
     * The interceptor is re-checked here, at click or submit time, and not only when arming, so a
     * route that is not in place fails closed (_routeArmedAdd) whatever armed the form. The same goes
     * for an offer Fly left out of its bundle map while the installed rewrite is Fly's stock one
     * (_flyStockRewriteDropsOffer, asked again here for the same reason): that rewrite cannot add it,
     * whether or not a selection for it exists. Nothing of Fly's state is written before these checks
     * pass. */
    if (!gfb || !gfb.utility || typeof gfb.utility.handleFBTAndProductBundleFormSubmit !== 'function') {
      return false;
    }
    if (this._flyInterceptorGap()) return false;
    const fbt = gfb.gfbFrequentBought;
    if (!fbt || !fbt.state) return false;
    const offerId = option.dataset.flyOfferId;
    if (!offerId) return false;
    if (this._flyStockRewriteDropsOffer(gfb, offerId)) return false;

    /* SET activeOffer here; do not verify it.
     *
     * Fly re-asserts activeOffer to whichever offer it actually rendered, so on a page with two
     * bundle offers the value _selectFlyOffer wrote is gone by the time anyone clicks. This used to
     * compare the two and bail on a mismatch, which meant the card the shopper had chosen was
     * rejected in favour of a widget they cannot see - observed as offerId FBT-704604 against
     * activeOffer ["FBT-051988"]. The click is the authority on what is being bought. */
    fbt.state.activeOffer = [offerId];

    /* Build the selection HERE if it is missing, rather than trusting an earlier step to have built
     * it. A load-time timer used to do that, and a click that landed before it ran found no selection
     * and failed closed - then the timer built one a moment later, leaving a working offer behind a
     * disabled button. Doing it at click time makes the add independent of timing. (_bundleRouteMissing
     * counts a selection this can build as ready, and asks _flyStockRewriteDropsOffer as this method
     * does, so an armed card never gets here and fails.) */
    let selected = fbt.state.currentSelectedVariants && fbt.state.currentSelectedVariants[offerId];
    if (!selected || !Object.keys(selected).length) {
      if (!this._adoptDroppedOffer(gfb, offerId, option)) return false;
      selected = fbt.state.currentSelectedVariants[offerId];
    }
    if (!selected || !Object.keys(selected).length) return false;

    this._setFlyColourVariant(selected, option);
    fbt.state.addBundleUsingNativeATC = true;
    return true;
  }

  /* Write the chosen colour straight into Fly's variant state.
   *
   * Every Fly path - the native rewrite, the widget button, the cart URL - builds its payload from
   * currentSelectedVariants, so this is the one place that decides which colour reaches the cart.
   * _syncFlyVariant tries to drive Fly's own radio instead and has never once succeeded on this
   * page; it logs "no Fly variant control found" on every render because the offer's variant UI is
   * not in .gfbCustomVariantContainer at all.
   *
   * Matched against ALL of the card's colour variant ids rather than just the default, because
   * after the first swap the entry no longer holds the default and a second colour change would
   * find nothing to replace. */
  _setFlyColourVariant(selected, option) {
    /* Only BUYABLE picks - _colourInputs skips disabled swatches. Returning early with no pick
     * leaves the entry holding whatever default it had, which is why neither add path is allowed to
     * run without a complete set of picks (_colourPicksComplete, checked in _apply and at click). */
    const chosen = this._colourInputs(option)
      .map((input) => Number(input.dataset.variantId))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (!chosen.length) return;

    /* Purge our own injected entries FIRST - before looking for the real component - so a previous
     * split cannot be mistaken for it, and so re-picking colours replaces rather than accumulates. */
    this._purgeInjectedUnits(selected);

    const known = this._colourVariantIds(option);
    if (!known.length) return;
    const baseKey = Object.keys(selected).find(
      (key) => selected[key] && known.indexOf(String(selected[key].id)) !== -1
    );
    if (!baseKey) return;
    const base = selected[baseKey];

    /* One shared choice: set the id and leave the quantity alone. Touching quantity here would turn
     * a 2-tumbler component into a 1-tumbler one. */
    const perUnit = this._perUnitColourCount(option);
    if (perUnit < 2) {
      base.id = chosen[0];
      return;
    }
    if (chosen.length !== perUnit) {
      /* A unit left unchosen would otherwise silently short the bundle. Fall back to one colour for
       * all of them rather than add fewer items than the card is charging for. */
      if (window.console) {
        console.warn('[fall-sale-picker] expected ' + perUnit + ' colours, got ' + chosen.length);
      }
      base.id = chosen[0];
      return;
    }

    /* Group identical colours instead of emitting a line each: two Lavenders are one line of two,
     * which is what Fly's own builder produces and what the cart should show. */
    const lines = [];
    chosen.forEach((id) => {
      const existing = lines.find((line) => line.id === id);
      if (existing) existing.quantity += 1;
      else lines.push({ id: id, quantity: 1 });
    });

    base.id = lines[0].id;
    base.quantity = lines[0].quantity;
    lines.slice(1).forEach((line, index) => {
      selected[UNIT_KEY + baseKey + ':' + (index + 2)] = Object.assign({}, base, {
        id: line.id,
        quantity: line.quantity,
      });
    });
  }

  /* Disable the button - which also drops the form to unarmed - and say so, rather than let a click
   * do something wrong. */
  _failClosed(option, message, why) {
    this._stopWatchingAdd();
    this._setBuyDisabled(true);
    if (this.pendingNote) {
      this.pendingNote.hidden = false;
      this.pendingNote.textContent = message;
    }
    if (window.console) {
      console.error(
        '[fall-sale-picker] no usable Fly path for ' + (option && option.dataset.flyOfferId) +
          (why ? ': ' + why : '') +
          '. Is the offer ACTIVE and inside its Fly schedule? A draft offer is absent from Fly\'s data, ' +
          'a switched-off one is listed with isActive false, and a bundle outside its schedule counts ' +
          'as switched off; none of them can be added.'
      );
    }
  }

  /* Refuse an add because what it would send cannot be bought. Separate from _failClosed, whose
   * console line blames the Fly offer - wrong here, and misleading to whoever reads it next. The
   * button is re-enabled by _apply the moment the shopper changes colour or card. */
  _blockAdd(message) {
    this._stopWatchingAdd();
    this._setBuyDisabled(true);
    if (this.pendingNote) {
      this.pendingNote.textContent = message;
      this.pendingNote.hidden = false;
    }
    if (window.console) {
      console.warn('[fall-sale-picker] add refused: it would have sent a sold-out or market-blocked variant.');
    }
  }

  /* ---------------------------------------------------------------------------------------------
   * Add REJECTIONS - see the note on installCartAddObserver at the top of this file for why.
   * ------------------------------------------------------------------------------------------- */
  _watchAddFailures(signal) {
    if (this._addWatchBound) return;
    this._addWatchBound = true;

    /* Any click on this picker's add button - main or sticky, however it is tied to the form - opens
     * a short window in which the next /cart/add response is read. Capture phase, and bound here in
     * connectedCallback, so it sees the click whichever add path then takes it; a click the picker
     * goes on to refuse closes the window again in _blockAdd. */
    document.addEventListener(
      'click',
      (event) => {
        const btn = event.target.closest && event.target.closest('[type="submit"], button[name="add"]');
        if (!btn || !this.form || btn.form !== this.form) return;
        this._expectAddResult();
      },
      { capture: true, signal }
    );

    /* A submit that is not a click on the button - requestSubmit(), a dispatched submit event - opens
     * the same window. (A click on the button opens it twice, once here and once above: harmless.) */
    document.addEventListener(
      'submit',
      (event) => {
        if (!this.form || event.target !== this.form) return;
        this._expectAddResult();
      },
      { capture: true, signal }
    );

    /* The theme's own failure event. Its message is usually just "Cart Error", so it is only used
     * when the response itself was not readable here; a specific message shown a moment ago wins. */
    if (this.form) {
      this.form.addEventListener('cart:error', () => {
        if (Date.now() - (this._addErrorAt || 0) < 2000) return;
        this._showAddError(null);
      }, { signal });
    }
  }

  _expectAddResult() {
    this._addWatchUntil = Date.now() + 15000;
    ADD_WATCHERS.add(this);
    installCartAddObserver();
  }

  _stopWatchingAdd() {
    this._addWatchUntil = 0;
    ADD_WATCHERS.delete(this);
  }

  /* Called with the promise a /cart/add fetch returned. Only the FIRST add after the click is this
   * picker's; anything later belongs to someone else (an upsell in the drawer, say) and is ignored. */
  _inspectAddResponse(promise) {
    const watching = Date.now() < (this._addWatchUntil || 0);
    this._stopWatchingAdd();
    if (!watching) return;

    Promise.resolve(promise).then(
      (response) => {
        if (!response) return;
        if (response.ok) {
          this._clearAddError();
          return;
        }
        /* Clone before anyone reads the body. This reaction was registered before the caller got
         * the promise back, so it runs first; if the body is somehow gone anyway, the generic
         * message still shows - an error without detail beats none. */
        let copy = null;
        try {
          copy = response.clone();
        } catch (error) {
          copy = null;
        }
        const show = (json) => this._showAddError(this._addErrorText(json));
        if (copy && typeof copy.json === 'function') copy.json().then(show, () => show(null));
        else show(null);
      },
      () => this._showAddError(null) // network failure: the theme throws and shows nothing at all
    );
  }

  /* Shopify's rejection puts the useful sentence in `description` ("You can't add more ... to the
   * cart."); `message` is usually the bare "Cart Error", which tells the shopper nothing. */
  _addErrorText(json) {
    if (json && typeof json.description === 'string' && json.description.trim()) return json.description.trim();
    if (json && typeof json.message === 'string' && json.message.trim() && !/^cart error$/i.test(json.message.trim())) {
      return json.message.trim();
    }
    return null;
  }

  /* Created on first use, so a page that never has a failed add carries no extra markup. role=alert
   * so the failure is announced, not only drawn. */
  _errorNode() {
    if (this._errorEl && this._errorEl.isConnected) return this._errorEl;
    const el = document.createElement('p');
    el.className = 'fall-picker__error';
    el.setAttribute('role', 'alert');
    el.dataset.fallError = '';
    el.hidden = true;
    this.appendChild(el);
    this._errorEl = el;
    return el;
  }

  _showAddError(text) {
    const el = this._errorNode();
    el.textContent = text || ADD_FAILED_TEXT;
    el.hidden = false;
    this._addErrorAt = Date.now();
    if (window.console) console.warn('[fall-sale-picker] add rejected: ' + el.textContent);
  }

  _clearAddError() {
    if (!this._errorEl) return;
    this._errorEl.hidden = true;
    this._errorEl.textContent = '';
  }

  /* Assemble state.currentSelectedVariants[offerId] from the card's own components, so an offer Fly
   * discarded can still be added through Fly's own rewrite path.
   *
   * The keys are ours and deliberately not product handles - nothing reads them as handles, exactly
   * as with the per-unit colour entries. Quantities and ids come from data-components, which Liquid
   * built from the same config that priced the card, so the payload and the advertised price cannot
   * disagree about what is in the bundle.
   *
   * Refuses to overwrite a selection Fly already made: if Fly kept this offer, its own object is
   * richer than ours and is left alone.
   *
   * Refuses, too, to build anything for an offer that is not LIVE (RFC 0006 section 3d, 3-FIX4). Fly's
   * one-bundle-per-page limit costs a second bundle card its selection and its widget, and with the
   * offers ON it costs it its place in the RUNTIME list gfb.settings.frequentBoughtData too: Fly's FBT
   * init trims that list to one offer (slice(0, 1)) before it emits its interceptor event. (The runtime
   * list holding all 20 offers on every one of the six preview pages was measured with the Fall offers
   * OFF, when Fly loads no FBT module and the trim never runs; it says nothing about offers ON.)
   * Liveness for a dropped second card therefore comes from Fly's FULL list, which the trim leaves
   * alone, plus Fly's own schedule test (_offerStatus) - so a genuinely active, in-schedule second
   * card is still live here and still gets its selection. A switched-off, out-of-schedule or missing
   * offer is not "dropped", it is unavailable, and giving it a selection
   * would only prepare a rewrite that charges the wrong price.
   *
   * Refuses, as well, an offer Fly left out of its bundle map while the installed rewrite is Fly's
   * STOCK one (_flyStockRewriteDropsOffer, RFC 0006 section 3-FIX5): that handler cannot build a
   * payload for such an offer and hands the form back unchanged, so a selection built here would go
   * into the cart as a plain add at full price. Only Santevia's custom-script rewrite, which reads
   * activeOffer and currentSelectedVariants and nothing else, can add it.
   *
   * This never enables a button either: only _armWhenReady arms the form. The one caller is
   * _armNativeFlyAdd, which _routeArmedAdd runs at add time - for a click or a submit alike - and only
   * after the guard let an armed add through. */
  _adoptDroppedOffer(gfb, offerId, option) {
    const fbt = gfb.gfbFrequentBought;
    if (!fbt || !fbt.state) return false;
    if (typeof gfb.utility.handleFBTAndProductBundleFormSubmit !== 'function') return false;
    if (this._flyStockRewriteDropsOffer(gfb, offerId)) return false;
    if (!this._offerIsLive(gfb, offerId, 'bundle')) return false;

    fbt.state.currentSelectedVariants = fbt.state.currentSelectedVariants || {};
    const existing = fbt.state.currentSelectedVariants[offerId];
    if (existing && Object.keys(existing).length) return true; // Fly got there first

    const built = this._selectionFromComponents(option);
    if (!built) return false;

    fbt.state.currentSelectedVariants[offerId] = built;
    this._setFlyColourVariant(built, option);
    if (window.console) {
      console.info(
        '[fall-sale-picker] Fly dropped ' + offerId +
          ' (it serves one bundle offer per page); selection rebuilt from the card, ' +
          Object.keys(built).length + ' component(s).'
      );
    }
    return true;
  }

  /* data-components is "variantId:qty,variantId:qty" - the same string the direct-add path used to
   * read, still emitted by Liquid for exactly this.
   *
   * Its colour id is the card's DEFAULT colour, which Liquid has already moved off a sold-out or
   * blocked pin. It is a starting point only: _adoptDroppedOffer runs _setFlyColourVariant over the
   * result straight away, which replaces it with the shopper's own picks, and the payload is checked
   * once more at click time. Components are never dropped here - that would short the bundle
   * against the price the card is charging. */
  _selectionFromComponents(option) {
    const raw = (option && option.dataset.components) || '';
    const entries = {};
    let index = 0;
    raw
      .split(',')
      .map((pair) => pair.trim())
      .filter(Boolean)
      .forEach((pair) => {
        const parts = pair.split(':');
        const id = Number(parts[0]);
        const quantity = Number(parts[1]) || 1;
        if (!Number.isFinite(id) || id <= 0) return;
        index += 1;
        entries[COMPONENT_KEY + index] = { id: id, quantity: quantity, isSelected: true, customField: '' };
      });
    return Object.keys(entries).length ? entries : null;
  }

  /* Where does this offer stand in Fly's data? 'live', 'inactive' (in the data but not active - or, for
   * a bundle, outside its schedule) or 'missing' (not in the data Fly has loaded, or that data is not
   * there yet).
   *
   * A DRAFT offer is simply absent from Fly's data, which leaves gfb loaded and every readiness check
   * passing - that is how a card came to advertise $103.98 and charge $129.98. A switched-off offer
   * is worse hidden: Fly still LISTS it, with isActive false (observed on the preview, 2026-09-29:
   * every Fall FBT offer and the Starter VOL offers), so mere presence proved nothing. Live means
   * present AND isActive === true - the boolean, not merely truthy.
   *
   * The two systems keep separate lists at runtime, both on gfb.settings:
   *   bundle (FBT-)  gfb.settings.frequentBoughtData
   *   volume (VOL-)  gfb.settings.volumeDiscountData
   * and Fly loads both from the page's inline window.fly_app_data.data, where they are named
   * frequentBoughtData and volumeBundleData.
   *
   * THE VOLUME LIST IS NOT THE WHOLE LIST. Checked against the six preview pages (2026-09-29): the
   * inline volumeBundleData always holds all 12 offers, switched-off ones included, and so does
   * frequentBoughtData (20). But gfb.settings.volumeDiscountData held all 12 on the Glass, Bath,
   * Mina and Gravity pages and ONE offer on the Shower Filter and Water Bottle Filter pages, even
   * with the Fall offers switched on in the inline data. That is Fly's own doing:
   * deriveWidgetDataForVolAndCheckOffersRelevantToCurrentPage (flyBundlesVolume.js) skips inactive
   * offers, keeps those that apply to the page's product, and then slice(0, 1) - one volume offer
   * per page - and writes the result back over gfb.settings.volumeDiscountData. (The full list
   * survives at gfb.settings.configDataFromDb.volumeDiscountData.) So an offer missing from the
   * runtime list is looked up in the inline list too: listed there and not active is 'inactive'
   * straight away, which is the true answer and lets the note show now instead of after the
   * six-second wait 'missing' costs. Present at runtime is decisive. For VOLUME, live requires the
   * runtime entry (the list Fly is acting on) with isActive === true; an ACTIVE offer that lost the
   * one-per-page slot is 'missing' here, and the card fails closed after the wait, as it always had to
   * at click.
   *
   * THE BUNDLE LIST IS NOT THE WHOLE LIST EITHER, and a bundle is judged differently (RFC 0006 section
   * 3-FIX4, 3-FIX5). With the Fall offers ON, Fly's FBT init runs
   * deriveWidgetDataForFBTAndCheckOffersRelevantToCurrentPage (flyBundlesFBT.js) before its first await,
   * and so before it emits the interceptor event: it skips inactive offers, skips offers that fail
   * validateOfferEligibilityForOffer, keeps those relevant to the page, then slice(0, 1) and writes that
   * one over gfb.settings.frequentBoughtData. The page's second bundle card is then absent from the
   * runtime list although its offer is active. Fly's untrimmed list survives at
   * gfb.settings.configDataFromDb.frequentBoughtData (a deep clone taken in setSettings). So a bundle is
   * looked up in the runtime list, then in that full list, and the FIRST entry found decides:
   * isActive === true AND inside its schedule (_flyScheduleEligible - Fly's own test, because it is the
   * other reason Fly leaves an offer out of its runtime list) is 'live'; found but not active, or outside
   * its schedule, is 'inactive'.
   *
   * The inline list (every offer, with its isActive and offerEligibilityConfig) is only a stand-in for a
   * full list that is not there: it is read ONLY when configDataFromDb.frequentBoughtData is not an array.
   * Once Fly's config is loaded it is authoritative, and an offer it lacks is 'missing' (a draft)
   * whatever the inline list says.
   *
   * 'live' does NOT mean Fly kept the offer. An offer Fly leaves out of its runtime list because the page
   * already has a more relevant one (the slice(0, 1) drop) is live, and its card DOES arm: the picker
   * builds the selection from the card (_adoptDroppedOffer), Santevia's Fly custom-script rewrite turns
   * it into the add, and the server-side Cart Transform, keyed on the _flyBundles properties that
   * rewrite stamps on each line, groups and discounts it. What such an offer needs is that rewrite and
   * not Fly's stock one, which is why _bundleRouteMissing and _adoptDroppedOffer ask
   * _flyStockRewriteDropsOffer. An offer outside its schedule, by contrast, is 'inactive' and never
   * arms. */
  _offerStatus(gfb, offerId, kind) {
    const find = (list) =>
      Array.isArray(list)
        ? list.find(
            (item) => item && (item.offerId === offerId || (item.metafieldData && item.metafieldData.offerId === offerId))
          )
        : null;
    const settings = gfb && gfb.settings;
    const inlineData = window.fly_app_data && window.fly_app_data.data;

    if (kind === 'volume') {
      const runtime = find(settings && settings.volumeDiscountData);
      if (runtime) return runtime.isActive === true ? 'live' : 'inactive';

      const inline = find(inlineData && inlineData.volumeBundleData);
      return inline && inline.isActive !== true ? 'inactive' : 'missing';
    }

    const fullList = settings && settings.configDataFromDb && settings.configDataFromDb.frequentBoughtData;
    const entry =
      find(settings && settings.frequentBoughtData) ||
      find(fullList) ||
      (Array.isArray(fullList) ? null : find(inlineData && inlineData.frequentBoughtData));
    if (!entry) return 'missing';
    return entry.isActive === true && this._flyScheduleEligible(entry) ? 'live' : 'inactive';
  }

  /* Is this Fly offer inside its schedule right now? Mirrors Fly's own validateOfferEligibilityForOffer
   * (flyBundlesFBT.js) on the entry's offerEligibilityConfig - the test Fly uses to keep an offer in or
   * out of its runtime list - so the answer is Fly's:
   *   no config                                 eligible
   *   type SCHEDULED, start AND end both set    eligible from the start to the end, inclusive
   *   any other type, start set and no end      eligible once the start has passed
   *   anything else                             eligible
   * It is a schedule check only: Fly reads the markets and customer tags in that config and tests
   * neither, so neither does this.
   *
   * It fails closed where Fly would shrug, and nothing in here can throw: a config or schedule that is
   * not an object, or is an array, is NOT eligible, and neither is a start or end that is present but
   * does not parse as a date. */
  _flyScheduleEligible(entry) {
    try {
      const config = entry && entry.offerEligibilityConfig;
      if (!config) return true;
      if (typeof config !== 'object' || Array.isArray(config)) return false;
      const schedule = config.schedule === undefined ? {} : config.schedule;
      if (!schedule || typeof schedule !== 'object' || Array.isArray(schedule)) return false;

      const start = schedule.startDateTimeUtc;
      const end = schedule.endDateTimeUtc;
      const from = start ? new Date(start) : null;
      const to = end ? new Date(end) : null;
      if ((from && isNaN(from.getTime())) || (to && isNaN(to.getTime()))) return false;

      const now = new Date();
      const scheduled = schedule.type === 'SCHEDULED';
      if (scheduled && from && to) return now >= from && now <= to;
      if (!scheduled && from && !to) return now >= from;
      return true;
    } catch (error) {
      return false;
    }
  }

  _offerIsLive(gfb, offerId, kind) {
    return this._offerStatus(gfb, offerId, kind) === 'live';
  }

  /* ---------------------------------------------------------------------------------------------
   * Cart-drawer saving
   *
   * UpCart renders a compare price as:
   *
   *   <span class="upcart-item-compare-price upcart-price-compare
   *                styles_Price__compareAtPrice__ UpcartDesignSettings__cartTextSubduedColor">
   *
   * so ours reuses those classes and inherits their styling rather than looking bolted on.
   *
   * Deliberate limits, because this reaches into another app's markup:
   *   - it only ADDS a node, never edits or removes one of theirs
   *   - if the row already has a compare price, it does nothing
   *   - if the markup is not what we expect, it does nothing and says so once
   *   - it works only where the picker is loaded, so /cart and checkout stay bare
   * ------------------------------------------------------------------------------------------- */

  /* Remember what this card's full price is, keyed by its offer, so the drawer can find it later -
   * including after a reload, when the page no longer knows what was added. */
  _rememberCompare(option) {
    const offerId = option && option.dataset.flyOfferId;
    const full = Number(option && option.dataset.fullCents) || 0;
    if (!offerId || !full) return;
    try {
      const store = JSON.parse(window.sessionStorage.getItem(COMPARE_STORE) || '{}');
      store[offerId] = full;
      window.sessionStorage.setItem(COMPARE_STORE, JSON.stringify(store));
    } catch (error) {
      /* Private mode, or storage disabled. The drawer simply will not show a saving. */
    }
  }

  _compareStore() {
    try {
      return JSON.parse(window.sessionStorage.getItem(COMPARE_STORE) || '{}');
    } catch (error) {
      return {};
    }
  }

  /* Watch for the drawer being drawn or redrawn. UpCart re-renders on every quantity change, so a
   * one-shot pass would be undone the first time a shopper touched a stepper. */
  _watchCartDrawer() {
    if (this._cartWatchBound) return;
    this._cartWatchBound = true;

    /* Kept on the instance so disconnectedCallback can stop the observer and the timer it schedules
     * with: a picker that has left the page has no business decorating the drawer. */
    this._drawerObserver = new MutationObserver(() => {
      window.clearTimeout(this._drawerTimer);
      this._drawerTimer = window.setTimeout(() => this._decorateCartDrawer(), 120);
    });
    this._drawerObserver.observe(document.body, { childList: true, subtree: true });
    this._decorateCartDrawer();
  }

  async _decorateCartDrawer() {
    const rows = document.querySelectorAll('[class*="upcart-product-item"]');
    if (!rows.length) return; // drawer not open, or not UpCart

    const store = this._compareStore();
    if (!Object.keys(store).length) return; // nothing of ours has been added

    /* Map each cart line's TITLE to its full price, via the offer id Fly stamps on the line.
     * Title is what the drawer row shows, so it is the join between cart data and DOM. */
    let cart;
    try {
      cart = await fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root.replace(/\/$/, '') : '') + '/cart.js').then((r) => r.json());
    } catch (error) {
      return;
    }

    const byTitle = {};
    (cart.items || []).forEach((item) => {
      const props = item.properties || {};
      const stamped = props.OfferId || props['_flyBundles:OfferId'];
      if (!stamped) return;
      const offerId = String(stamped).split('_')[0];
      const perUnit = store[offerId];
      if (!perUnit) return;

      /* Compare against the LINE, not the unit. UpCart shows the line total, so at quantity 2 a
       * per-unit compare reads $354.94 beside $532.42 - a strikethrough LOWER than the price, which
       * is worse than showing none at all. */
      const quantity = item.quantity || 1;
      const lineFull = perUnit * quantity;
      const linePrice = item.final_line_price || item.line_price || item.price * quantity;

      /* Only ever show a saving that is actually a saving. */
      if (lineFull > linePrice) byTitle[(item.title || '').trim()] = lineFull;
    });
    if (!Object.keys(byTitle).length) return;

    rows.forEach((row) => {
      const existing = row.querySelector('[class*="upcart-item-compare-price"]');
      /* Exclude the ROW. "upcart-item-prices-row" contains "upcart-item-price" as a substring, so a
       * loose [class*=] match returns the container and the compare price lands above the price
       * instead of beside it. */
      const priceEl = row.querySelector(
        '[class*="upcart-item-price"]:not([class*="upcart-item-prices-row"])'
      );
      const titleEl = row.querySelector('[class*="title"], [class*="Title"]');
      if (!priceEl || !titleEl) return;

      /* The row's title node also carries the "Show N items" disclosure, so match by prefix. */
      const text = (titleEl.textContent || '').trim();
      const key = Object.keys(byTitle).find((t) => t && text.indexOf(t) === 0);
      if (!key) return;

      /* A compare price is already there. Leave a CORRECT one alone - that is UpCart doing its job.
       * But Fly writes a nonsense value onto the bundle parent product ($24.99 on the bath-filter
       * bundle, $19.99 on the WBF one - each the price of a single component), and UpCart renders it
       * faithfully. A strikethrough BELOW the price reads as a price increase, which is worse than
       * showing nothing, so that one gets its text corrected. Overwriting the text rather than
       * replacing the node keeps their element and their styling. */
      if (existing) {
        const shown = this._parseMoney(existing.textContent);
        const linePrice = this._parseMoney((priceEl.textContent || '').trim());
        if (shown === null || linePrice === null || shown > linePrice) return; // theirs is fine
        existing.textContent = this._formatMoney(byTitle[key]);
        existing.dataset.fallSaleCompare = 'corrected';
        return;
      }

      const span = document.createElement('span');
      span.className =
        'upcart-item-compare-price upcart-price-compare styles_Price__compareAtPrice__ ' +
        'UpcartDesignSettings__cartTextSubduedColor';
      span.dataset.fallSaleCompare = 'true';
      /* UpCart's own compare price gets its spacing from the row it sits in; inserting into that
       * row does not reproduce it, so the two prices render flush - "$494.94$371.21". A margin on
       * ours is the smallest fix that does not touch their styles. */
      span.style.marginRight = '0.4em';
      span.textContent = this._formatMoney(byTitle[key]);
      priceEl.parentNode.insertBefore(span, priceEl);
    });
  }

  /* Remove the entries we injected, leaving Fly's own untouched. */
  _purgeInjectedUnits(entries) {
    if (!entries) return;
    Object.keys(entries).forEach((key) => {
      if (key.indexOf(UNIT_KEY) === 0) delete entries[key];
    });
  }

  /* Fly's widget button, used for VOLUME cards.
   *
   * It needs no merchant flag, no unticked setting and no payload of ours - it is the widget, so
   * "Apply Discount only if added from widget" is satisfied by definition, and Fly does its own
   * validation, its own cart-transform bookkeeping and its own post-add behaviour from the Checkout
   * Settings. The native-rewrite path this replaces depended on merchantInfo.isNativeATCEnabledForVolume,
   * which is account-level and cannot be switched on from the admin at all.
   *
   * Returns the button to click, after putting Fly's state where the card says it should be, or
   * null if anything is missing - in which case the caller (_routeArmedAdd, for a volume card only)
   * fails the card closed rather than clicking a button that would add the wrong thing. A bundle card
   * is never routed here: Fly's FBT button clicks the theme's native add button again, so there is no
   * widget fallback for a bundle. */
  _readyFlyWidgetButton(option) {
    const offerId = option && option.dataset.flyOfferId;
    if (!offerId) return null;
    const button = this._flyWidgetButton(option);
    if (!button) return null;

    if (this._offerKind(option) === 'volume') {
      const vol = window.gfb && window.gfb.gfbVolumeDiscount;
      if (!vol || !vol.state) return null;
      /* prepareCartPayloadForQuantityBreaks reads both of these; a tier that does not match this
       * card would add a different quantity at a different discount. */
      vol.state.activeOffer = [offerId];
      if (!this._selectVolumeTier(offerId, Number(option.dataset.units) || 0, option)) return null;
    }
    return button;
  }

  /* Let go of BOTH of Fly's active offers before every selection, and let each card re-arm only
   * what it needs.
   *
   * Fly has two independent form-submit hooks and each rewrites a plain native add while its own
   * offer is left active:
   *
   *     FBT     if (activeOffers.length == 0 || !addBundleUsingNativeATC) return null;
   *     VOLUME  if (activeOffers.length == 0 || !isNativeATCEnabledForVolume
   *                                          || addBundleUsingNativeATC) return null;
   *
   * The previous commit cleared only the bundle side, which is why the single-filter card kept
   * adding three: choosing the 3-Pack left VOL-172786 active, and the volume hook then rewrote the
   * single-product add into the tier payload. Re-asserting the form quantity could never fix that -
   * the quantity was never the thing being read.
   *
   * Clearing both is cheap. A bundle card re-arms the FBT side through _selectFlyOffer, and a
   * volume card re-arms its own side at click time in _readyFlyWidgetButton. */
  _releaseFlyState() {
    const gfb = window.gfb;
    const fbt = gfb && gfb.gfbFrequentBought;
    if (fbt && fbt.state) {
      fbt.state.activeOffer = [];
      fbt.state.addBundleUsingNativeATC = false;
      /* Drop any per-unit colour entries we injected. They belong to one card's selection, and Fly
       * re-renders its widget from this object - leaving ours behind would have it draw components
       * that are not in any offer. */
      const byOffer = fbt.state.currentSelectedVariants || {};
      Object.keys(byOffer).forEach((offerId) => this._purgeInjectedUnits(byOffer[offerId]));
    }
    const vol = gfb && gfb.gfbVolumeDiscount;
    if (vol && vol.state) vol.state.activeOffer = [];
  }

  /* Fly's own add-to-cart button for this offer, inside the widget we hide. Only VOLUME callers use
   * it: _volumeRouteMissing asks whether it is there and _readyFlyWidgetButton hands it to
   * _routeArmedAdd to click. A bundle card never clicks it (Fly's FBT button re-enters the theme's
   * native add), so nothing falls back to it for a bundle.
   *
   * Clicking it is the most faithful "added from widget" there is - it IS the widget - so no
   * merchant flag and no unticked setting are needed for the discount to apply. Its handler reads
   * the Checkout Settings for that module:
   *
   *     let executeDefaultSideCartUpdate_volumeBundle = gfb.settings.app.executeDefaultSideCartUpdate_volumeBundle;
   *     ...
   *     const items = gfb.gfbVolumeDiscount.utility.prepareCartPayloadForQuantityBreaks(offerId);
   *     response = await gfb.utility.addToCart({ items, "section-id": gfb.state.section_id });
   *
   * so with Checkout Settings on "Open Side Cart Drawer" it adds, opens the drawer and stays put.
   * It also runs Fly's own out-of-stock and cart validation first, which our direct add never did.
   *
   * display:none does not stop a programmatic click - the handler is bound by delegation, not by
   * visibility. Matched on the offer id plus an AddToCart-ish class; the list holds the bundle
   * widget's classes as well as the volume widget's, without hardcoding two selectors that Fly may
   * rename, but only volume callers act on the result.
   *
   * `quiet` is for the arming poll, which asks this every ARM_POLL_MS until Fly has rendered the
   * button: an absent button then is the expected answer, not something to warn about each time. */
  _flyWidgetButton(option, quiet) {
    const offerId = option && option.dataset.flyOfferId;
    if (!offerId) return null;
    /* Fly's own class names, read from its modules rather than pattern-matched. A loose /addtocart/
     * regex also matches wrappers, popups and the disabled-state class, and clicking one of those
     * does nothing at all - which looks exactly like a broken button. */
    const classes = [
      'gfbVolumeDiscountWidgetOfferAddToCartButton',
      'gfbFrequentBoughtAddToCartButton',
      'gfbFooterAddToCartBtn_Template1',
      'gfbFooterAddToCartBtn_Template2',
    ];
    const selector = classes.map((name) => '.' + name + '[data-offer-id="' + offerId + '"]').join(',');
    const button = document.querySelector(selector);
    if (!button && !quiet && window.console) {
      console.warn('[fall-sale-picker] no Fly widget button for ' + offerId + '; tried ' + selector);
    }
    return button;
  }

  /* Which tier of a volume offer means `units`: the offer's rules, and the 0-based index of the rule
   * whose conditionValue equals it (-1 when none does). Reads Fly's data and changes nothing, so the
   * arming check and _selectVolumeTier - which does write Fly's state - ask exactly the same
   * question and cannot disagree about which tier a card means. */
  _volumeTier(offerId, units) {
    const offers = (window.gfb && window.gfb.settings && window.gfb.settings.volumeDiscountData) || [];
    const offer = (Array.isArray(offers) ? offers : []).find(
      (item) => item && (item.offerId === offerId || (item.metafieldData && item.metafieldData.offerId === offerId))
    );
    const rules = (offer && offer.rules) || [];
    return { rules: rules, index: rules.findIndex((rule) => Number(rule && rule.conditionValue) === units) };
  }

  /* Does Fly hold the tier this card means? The same two things _selectVolumeTier needs at click time
   * (a matching rule, and the tier's variants in Fly's state), checked without changing anything -
   * the volume half of "the add route is ready". */
  _volumeTierReady(state, offerId, units) {
    if (!state || !units) return false;
    const found = this._volumeTier(offerId, units);
    if (found.index === -1) return false;
    const variants = state.currentSelectedVariantsForTiers && state.currentSelectedVariantsForTiers[offerId];
    return Boolean(variants && variants['tier-' + (found.index + 1)]);
  }

  /* Point Fly's volume state at the tier this card represents.
   *
   * tierCount is a 1-BASED INDEX held as a string, not a quantity - Fly builds it as
   * `${activeTierIndex + 1}` - and qtyLabel carries the rule's conditionValue. So the tier is found
   * by matching our own unit count against the offer's rules rather than by arithmetic on the id.
   *
   * Fly populates currentSelectedVariantsForTiers as it renders each tier; if the one we want is
   * missing, its payload builder would return an empty list, so this fails closed. */
  _selectVolumeTier(offerId, units, option) {
    const state = window.gfb.gfbVolumeDiscount.state;
    if (!units) return false;

    const { rules, index } = this._volumeTier(offerId, units);
    if (index === -1) {
      if (window.console) {
        console.warn('[fall-sale-picker] no ' + units + '-unit tier on ' + offerId, rules);
      }
      return false;
    }

    const tierKey = 'tier-' + (index + 1);
    const variants = state.currentSelectedVariantsForTiers && state.currentSelectedVariantsForTiers[offerId];
    if (!variants || !variants[tierKey]) return false;

    state.currentSelectedTiers = state.currentSelectedTiers || {};
    state.currentSelectedTiers[offerId] = {
      qtyLabel: rules[index].conditionValue,
      tierCount: String(index + 1),
    };

    this._setVolumeColourVariant(offerId, tierKey, option);
    return true;
  }

  /* Colour for a QUANTITY BREAK card. Deliberately not _setFlyColourVariant - the two Fly systems
   * do not share a shape, and reusing the bundle matcher here silently changed nothing:
   *
   *   FBT     currentSelectedVariants[offerId][handle]      { id, quantity, isSelected }
   *   VOLUME  currentSelectedVariantsForTiers[offerId][tier] { "0": { variantId, qty, price, ... } }
   *
   * Different field names, numeric keys, and no isSelected. The bundle matcher looks for `.id`,
   * matched nothing, and returned quietly - so White was chosen, Black was added, and the price
   * stayed correct. A colour bug that leaves the price right is the worst kind: nothing on the page
   * or in the cart looks wrong, and the shopper finds out when the parcel arrives.
   *
   * Fly keeps the colour NAME separately in currentSelectedOptionsForTiers, so both are updated -
   * the payload builder reads variantId, but leaving the options list saying "Black" would make
   * Fly's own UI and validation disagree with what is being bought. */
  _setVolumeColourVariant(offerId, tierKey, option) {
    const state = window.gfb && window.gfb.gfbVolumeDiscount && window.gfb.gfbVolumeDiscount.state;
    if (!state) return;
    const byTier = state.currentSelectedVariantsForTiers && state.currentSelectedVariantsForTiers[offerId];
    const tier = byTier && byTier[tierKey];
    if (!tier) return;

    const colour = this._colourInput(option);
    const chosenId = colour && Number(colour.dataset.variantId);
    if (!chosenId) return;
    const known = this._colourVariantIds(option);
    if (!known.length) return;

    const label = colour.parentElement && colour.parentElement.querySelector('.fall-picker__colour-name');
    const chosenName = label ? label.textContent.trim() : null;

    const optionsByTier = state.currentSelectedOptionsForTiers && state.currentSelectedOptionsForTiers[offerId];
    const tierOptions = optionsByTier && optionsByTier[tierKey];

    Object.keys(tier).forEach((key) => {
      const entry = tier[key];
      /* Match on ANY of the card's colour ids, not just the configured default - after one change
       * the entry no longer holds the default. */
      if (!entry || known.indexOf(String(entry.variantId)) === -1) return;
      entry.variantId = chosenId;
      if (chosenName && tierOptions && tierOptions[key] && Array.isArray(tierOptions[key].options)) {
        tierOptions[key].options = [chosenName];
      }
    });
  }

  /* The page renders its price in Liquid so there is no flash and it is market-correct. This checks
   * that number against what Fly will actually charge, and corrects it if they disagree - so a
   * change in Fly's rounding, or a component price moving mid-session, cannot leave the page
   * advertising a price the cart will not honour. */
  _reconcilePrice(gfb, offerId, option, attempt) {
    try {
      /* Bundle offers only. getBundleTotalDiscountedAndStrikeThroughPrice looks the id up in
       * frequentBoughtData, so a VOL- id makes it dereference undefined and throw (observed on the
       * WBF page). Volume pricing is verified against the offer's own tier rules instead. */
      if (this._offerKind(option) !== 'bundle') return;
      if (!this.isConnected) return; // the picker left the page while we waited: nothing to price
      if (this.selected !== option) return; // selection moved on while we waited
      const priced = gfb.utility && gfb.utility.getBundleTotalDiscountedAndStrikeThroughPrice;
      if (typeof priced !== 'function') return;

      /* frequentBoughtData is filled in asynchronously, AFTER gfb.gfbFrequentBought exists - so the
       * readiness gate that got us here is not enough on its own. Calling the pricer before this
       * offer lands in it makes it dereference undefined and throw, which showed up as a misleading
       * error on every single page load. Wait for the offer rather than for Fly in general. */
      const offers = (gfb.settings && gfb.settings.frequentBoughtData) || [];
      const known =
        Array.isArray(offers) &&
        offers.some(
          (offer) =>
            offer &&
            (offer.offerId === offerId || (offer.metafieldData && offer.metafieldData.offerId === offerId))
        );
      if (!known) {
        const tries = attempt || 0;
        if (tries < 20) {
          window.setTimeout(() => this._reconcilePrice(gfb, offerId, option, tries + 1), 150);
        }
        return;
      }

      const result = priced.call(gfb.utility, offerId);
      const flyCents = this._parseMoney(result && result.totalDiscountedPrice);
      if (flyCents === null) return;

      const shownCents = Number(option.dataset.saleCents) || 0;
      if (flyCents === shownCents) return;

      /* ONLY rounding drift may override Liquid. A large gap means Fly's QUOTE is wrong, not ours.
       *
       * Observed 2026-08-28, UK gravity-water-system top card, offer FBT-028974:
       *   Liquid            15998c  (£199.98 x 0.80 — the market price list)
       *   Fly's pricer      17358c  <- this code used to overwrite the page with it
       *   Fly's CHARGE      15998c  (confirmed in the cart)
       * Fly quoted a saving of £26.40, which is 20% of £132.00 — the base total CONVERTED to GBP —
       * and subtracted it from the UK price-list full of £199.98. Two currency bases in one sum.
       * Its checkout discount used the price list correctly, so the quote and the charge disagreed
       * and we were trusting the wrong one, advertising £173.58 against a £159.98 charge.
       *
       * It surfaces only where a market's price list is not a straight conversion of base. CA is
       * the base; US prices for these components equal CA's; UK is priced deliberately higher, so
       * UK is the market where the two diverge. /en-intl is unverified.
       *
       * Liquid is computed from `all_products`, which IS the market price list, so on a large gap
       * it is the trustworthy number and Fly's quote is not. Keep ours and say so loudly.
       */
      const delta = Math.abs(flyCents - shownCents);
      if (delta > RECONCILE_MAX_DELTA_CENTS) {
        if (window.console) {
          console.warn(
            '[fall-sale-picker] REFUSING Fly price for offer ' + offerId + ': page ' + shownCents +
              'c vs Fly quote ' + flyCents + 'c (' + delta + 'c apart, cap ' + RECONCILE_MAX_DELTA_CENTS +
              'c). Too large to be rounding, so keeping the Liquid price, which is computed from ' +
              'this market\'s price list. If the CART charges ' + flyCents + 'c, this is a real ' +
              'defect — check the offer and the market price list.'
          );
        }
        return;
      }

      if (window.console) {
        console.warn(
          '[fall-sale-picker] price mismatch on offer ' + offerId +
            ': page showed ' + shownCents + 'c, Fly charges ' + flyCents + 'c. Correcting to Fly.'
        );
      }
      option.dataset.saleCents = String(flyCents);
      const priceEl = option.querySelector('.fall-picker__price');
      if (priceEl) priceEl.textContent = this._formatMoney(flyCents);
      const saveEl = option.querySelector('.fall-picker__save');
      const full = Number(option.dataset.fullCents) || 0;
      if (saveEl && full > flyCents) {
        const saved = full - flyCents;
        saveEl.textContent = 'Save ' + this._formatMoney(saved) + ' (' + Math.round((saved / full) * 100) + '%)';
      }
      this._updateHeadlinePrice(flyCents, full);
    } catch (error) {
      if (window.console) console.warn('[fall-sale-picker] price reconcile failed', error);
    }
  }

  /* Fly returns a formatted money string. Strip everything but digits and the decimal separator. */
  _parseMoney(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/[^0-9.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.');
    const value = parseFloat(cleaned);
    return Number.isFinite(value) ? Math.round(value * 100) : null;
  }

  /* The hidden `id` input is rendered by the buy_buttons block, not by variant_picker — so it is
   * present even though variant_picker is disabled on these templates. */
  _setFormVariant(variantId) {
    if (!this.form) return;
    const input = this.form.querySelector('input[name="id"]');
    if (input && input.value !== String(variantId)) {
      input.value = String(variantId);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /* Uses form.elements, NOT form.querySelectorAll. querySelectorAll only finds DESCENDANTS, and the
   * sticky add-to-cart buttons live OUTSIDE the form, associated to it by a `form=` attribute
   * (snippets/product-sticky-add-to-cart.liquid:57-58). form.elements includes form-associated
   * elements, so it catches them. With querySelectorAll the sticky button stayed ENABLED on a
   * bundle-pending option - a fail-OPEN on the exact guarantee this design claims.
   *
   * This is also where the form is armed and disarmed (RFC 0006 section 3c): data-fall-armed is
   * written from the same flag as the buttons' disabled state, in the same call, so what the shopper
   * sees and what the add-to-cart guard enforces are one fact and cannot drift apart. Disabling
   * writes the marker FIRST and enabling writes it LAST, so if anything in between ever threw, the
   * form would be left blocked rather than open. Every fail-closed path in this file already goes
   * through here, which is why none of them needs its own disarm.
   *
   * A picker that is not connected can be disarmed but never armed, whatever asked: this is the one
   * place every arm goes through, so the check is here rather than in each caller. (A wait that
   * outlives the picker - a Fly poll, a gallery upgrade - would otherwise re-arm a form after
   * disconnectedCallback had blocked it.) */
  _setBuyDisabled(disabled) {
    this._setFormState(this.form, Boolean(disabled) || !this.isConnected);
  }

  /* The body of _setBuyDisabled, for any form: also how disconnectedCallback unarms a form that is
   * no longer this picker's own `form`. */
  _setFormState(form, flag) {
    if (!form || !form.elements) return;
    if (flag) form.setAttribute('data-fall-armed', 'false');
    Array.from(form.elements).forEach((el) => {
      const isSubmit =
        el.type === 'submit' ||
        (el.tagName === 'BUTTON' && (el.name === 'add' || el.type === 'submit')) ||
        (el.classList && el.classList.contains('button--add-to-cart'));
      if (!isSubmit) return;
      el.disabled = flag;
      el.setAttribute('aria-disabled', String(flag));
    });
    if (!flag) form.setAttribute('data-fall-armed', 'true');
  }

  /* Best-effort. The theme dispatches no price event and nothing listens for one, so there is no
   * hook to reuse — we write the rendered price node directly. If the markup is not what we expect
   * we leave it alone rather than guess: a stale headline price is bad, a mangled one is worse. */
  _updateHeadlinePrice(saleCents, fullCents) {
    if (!saleCents) return;
    const host = document.querySelector('.product-info .price-list, .product-info price-list');
    if (!host) return;

    /* Target the ELEMENT names, not the classes. snippets/price-list.liquid:64 only applies
     * `text-on-sale` when the variant itself is on sale (compare_at > price) - MINA Pitcher is not,
     * so a class-based selector silently matched nothing and the headline price never moved. */
    const sale = host.querySelector('sale-price');
    const compare = host.querySelector('compare-at-price');
    const money = (cents) => this._formatMoney(Number(cents));

    if (sale) sale.textContent = money(saleCents);
    if (compare && fullCents && Number(fullCents) > Number(saleCents)) {
      compare.textContent = money(fullCents);
      compare.hidden = false;
      /* snippets/price-list.liquid renders <compare-at-price> on EVERY product page, but stamps an
       * inline style="display:none" on it when the variant itself is not on sale - which no bundle
       * parent is. An inline style beats the hidden attribute, so clearing hidden alone left the
       * headline with no strikethrough while every card underneath showed one. Clear both. */
      compare.style.display = '';
    } else if (compare) {
      compare.hidden = true;
      compare.style.display = 'none';
    }
  }

  /* Uses the shop's own money format when the theme exposes it, so we do not invent a currency
   * presentation that disagrees with every other price on the page. */
  _formatMoney(cents) {
    const format =
      (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
      (window.Shopify && window.Shopify.money_format) ||
      '';
    const amount = (cents / 100).toFixed(2);
    if (format && format.indexOf('{{') !== -1) {
      return format.replace(/\{\{\s*[a-z_]+\s*\}\}/i, amount);
    }
    return '$' + amount;
  }

  /* ---------------------------------------------------------------------------------------------
   * CARD GALLERY (RFC 0005 §3a-FIX1 / D-A285, amended by §3a-FIX2 / D-A286). A card whose
   * galleryProduct resolved swaps the WHOLE gallery for the parent bundle product's own - every
   * photo, the theme's own dots, swipe and zoom - not just the main slide (CARD IMAGE below does
   * that one-slide swap, for the refill cards). This is the single entry point wherever
   * _showCardImage(option) used to be called directly: it picks one of the two behaviours and, for
   * the "no gallery" case, falls through to _showCardImage itself so that path is completely
   * unchanged.
   *
   * A parent gallery reaches the document one of two ways, and this reads either the same way:
   *   - the Liquid-DEFAULT card's, already sitting in the document with data-fall-gallery-card set,
   *     moved into place by the inline mover script in fall-sale-picker.liquid. That script runs
   *     synchronously, the instant the HTML parser reaches it; fall-sale-picker.js loads with the
   *     ordinary `defer` attribute, so - by nothing more exotic than how `defer` always works - this
   *     module never runs until after every such inline script on the page already has;
   *   - any other resolving card's, imported ONCE from its own template[data-fall-card-gallery] (a
   *     direct child of the card's <label>) the first time that card is selected.
   * Either way the node is found again next time by the same attribute (product-gallery[data-fall-
   * gallery-card="<id>"]), so nothing is ever imported twice, and a gallery is only ever shown or
   * hidden after that - never removed, replaced or re-inserted (AC-5).
   *
   * Every parent gallery also binds to a dedicated INERT form (fall_gallery_form_id in
   * fall-sale-picker.liquid), never the page's own product form - a fix cycle 1 change (RFC 0005
   * §3a-FIX1 item 1): a parent bound to the real form used to pick up ProductGallery's own
   * product:rerender / variant:change listeners (theme.js), so a theme or app rerender of the page
   * could filter or duplicate it. Bound to a form that is never submitted and never dispatches
   * either event, a parent gallery is inert to both, which is why _findGallery always finds the PAGE
   * gallery even after several cards have been visited - belt-and-braces, its own query also still
   * excludes anything already carrying data-fall-gallery-card.
   *
   * Every parent gallery also carries data-fall-gallery-owner, this picker's own `id` (RFC 0005
   * §3a-FIX2 item 1): every find/hide/reveal query below is scoped to it, and to this picker's own
   * page gallery, so a second fall-sale-picker elsewhere on the same page - and its own galleries -
   * can never be found, hidden or revealed by this one, or vice versa. The Liquid-default gallery is
   * stamped with the owner id by the mover script in fall-sale-picker.liquid (this module has not
   * run yet when that script does); every other one is stamped here, in _importCardGallery, since
   * this module is what imports it.
   *
   * Nothing here dispatches variant:change or product:rerender, and every gallery's own buttons
   * (zoom, carousel nav, thumbnails) sit outside every <form> and carry no form attribute of their
   * own, so a gallery choice can never reach the buy form, Fly's state or the cart - only
   * carousel:settle, the same re-measure signal _showCardImage already dispatches on its own slot's
   * carousel.
   * ------------------------------------------------------------------------------------------- */
  _showCardGallery(option) {
    const token = (this._galleryToken = (this._galleryToken || 0) + 1);

    if (!this.gallery) {
      /* No page gallery on this product at all (RFC 0005 §3a-FIX1 item 6, "S2"): nothing to import
       * a parent into or swap with, so every bundle card behaves exactly like a Starter here -
       * _showCardImage is already a safe no-op with no gallery (_cardImageSlot caches null), so this
       * is simply skipping the whole import path, not adding a second no-gallery branch to it. */
      this._showCardImage(option);
      return;
    }

    const cardId = option ? option.dataset.fallOption || '' : '';
    const existing = cardId ? this._findCardGallery(cardId) : null;

    if (existing) {
      const state = existing.getAttribute('data-fall-gallery-state');
      if (state === 'unchecked') {
        /* The Liquid-default card's own gallery (RFC 0005 §3a-FIX2 item 6): the mover already put it
         * on screen, during parsing, before this module ever ran - but it never checked whether its
         * first photo actually decodes. This is that check, run once, the first time this card is
         * shown here. Nothing more to do meanwhile: the gallery is already the one visible. */
        this._checkDefaultGallery(existing, option, token);
        return;
      }
      if (state === 'failed') {
        /* This card's parent photo could not be shown the first time it was tried. Treated exactly
         * like no gallery on every later visit (RFC 0005 §3a-FIX1 item 5): the broken gallery is
         * never revealed again, it just stays hidden forever (never removed, per AC-5). */
        this._hideAllCardGalleries();
        this.gallery.hidden = false;
        this._showCardImage(option);
        return;
      }
      if (state === 'pending') {
        /* A pending gallery re-selected - including a colour or quantity change that re-applies the
         * SAME card while its first photo is still decoding - awaits the SAME load rather than
         * revealing early or starting a second import. Token-guarded, so if the shopper has since
         * moved on to a different card, this stale callback does nothing when the wait finishes; the
         * connection guard covers the same picker instance having since been replaced by a rerender
         * (RFC 0005 §3a-FIX2 item 5) - whichever instance is current when the promise settles is the
         * one that acts, and GALLERY_LOADS is keyed by the gallery element itself, not by this
         * instance, so a REPLACEMENT instance can still find and await it. */
        const pending = GALLERY_LOADS.get(existing);
        if (pending) {
          pending.then(() => {
            if (!this.isConnected) return;
            if (token !== this._galleryToken) return; // a later selection already won the race
            if (existing.getAttribute('data-fall-gallery-state') === 'ready') {
              this._revealCardGallery(existing, token);
            } else {
              this._hideAllCardGalleries();
              if (this.gallery) this.gallery.hidden = false;
              this._showCardImage(option);
            }
          });
          return;
        }
        // No load on record for a "pending" gallery should not happen; fall through and reveal it
        // rather than stranding the card on Starter forever.
      }
      this._revealCardGallery(existing, token);
      return;
    }

    const tpl = cardId ? option.querySelector(':scope > template[data-fall-card-gallery]') : null;
    if (tpl) {
      this._importCardGallery(cardId, tpl, option, token);
      return;
    }

    /* Starter, refill, or a bundle whose galleryProduct is blank, Draft or otherwise unresolved:
     * every parent gallery goes back to hidden, the page gallery returns, and CARD IMAGE (#1) below
     * runs exactly as it did before this feature existed. */
    this._hideAllCardGalleries();
    if (this.gallery) this.gallery.hidden = false;
    this._showCardImage(option);
  }

  /* The already-known gallery for a card id - the fastest path, and also every second-or-later visit
   * to a card that was imported before. Scoped to THIS picker's own owner id (RFC 0005 §3a-FIX2
   * item 1: data-fall-gallery-owner, this.id) as well as the card id, so a card id that happened to
   * collide with a different fall-sale-picker elsewhere on the same page could never match that
   * other picker's gallery instead of this one's - or lack of one - being reported here. */
  _findCardGallery(cardId) {
    if (!cardId) return null;
    return document.querySelector(
      'product-gallery[data-fall-gallery-owner="' + this.id + '"][data-fall-gallery-card="' + cardId + '"]'
    );
  }

  /* Every parent gallery THIS picker owns (RFC 0005 §3a-FIX2 item 1) - never a different picker's. */
  _hideAllCardGalleries() {
    document.querySelectorAll('product-gallery[data-fall-gallery-owner="' + this.id + '"]').forEach((gallery) => {
      gallery.hidden = true;
    });
  }

  /* Show `gallery`: hide the page gallery and every other parent gallery in the same step, then
   * dispatch carousel:settle on this one's own carousel. The carousel's ResizeObserver (theme.js
   * ScrollCarousel) re-syncs the scroll position itself the moment `hidden` clears and it measures a
   * real size again; carousel:settle is the one signal that does NOT follow from that alone - it is
   * what ProductGallery's own onMediaSettle listens for (AR button state, video/model autoplay) and
   * what adaptive-height carousels re-measure on - so it is dispatched by hand here, exactly the way
   * _showCardImage already dispatches it after its own in-place swap. */
  _revealCardGallery(gallery, token) {
    if (token !== this._galleryToken) return; // a later selection already won the race
    if (this.gallery) this.gallery.hidden = true;
    this._hideAllCardGalleries();
    gallery.hidden = false;
    const carousel = gallery.querySelector('.product-gallery__carousel');
    if (carousel) {
      carousel.dispatchEvent(
        new CustomEvent('carousel:settle', { detail: { index: carousel.selectedIndex, cell: carousel.selectedCell } })
      );
    }
  }

  /* The inert form every parent gallery binds to (RFC 0005 §3a-FIX1 item 1), created here for every
   * card gallery THIS module imports - the prehide script creates the same one for the Liquid-
   * default card, which is already in the document (see fall-sale-picker.liquid) before this module
   * ever runs. ProductGallery's own connectedCallback (theme.js) does
   * document.forms[this.getAttribute("form")].addEventListener(...) the moment the element connects
   * to the document, which throws if that form does not exist yet - so this has to run before the
   * gallery is ever inserted, never after. `id` is read straight off the imported gallery's own
   * `form` attribute (product_form_id from Liquid), never recomputed here, so there is exactly one
   * place that knows the id's shape. */
  _ensureGalleryForm(id) {
    if (!id || document.forms[id]) return;
    const form = document.createElement('form');
    form.id = id;
    form.hidden = true;
    document.body.appendChild(form);
  }

  /* The gallery's own initial-slide <img> - the same cell CARD IMAGE's _cardImageSlot looks for
   * (`.is-initial`, falling back to the first cell when nothing is marked initial), shared here so
   * _importCardGallery and _checkDefaultGallery run the exact same lookup rather than two versions
   * that could drift apart. Null when the initial cell is not an image at all (a video or a model) -
   * a gallery whose FIRST cell can never fail to decode gets treated as ready outright. */
  _initialGalleryImage(carousel) {
    return (
      (carousel && carousel.querySelector(':scope > .product-gallery__media.is-initial:not([hidden]) > img')) ||
      (carousel && carousel.querySelector(':scope > .product-gallery__media:not([hidden]) > img')) ||
      null
    );
  }

  /* Decode `img`, `gallery`'s own initial photo, and call `onReady` once it is genuinely showable or
   * `onFail` if it is broken - shared by _importCardGallery (a freshly imported template) and
   * _checkDefaultGallery (the Liquid-default gallery the mover already put on screen). Tracks the
   * promise in GALLERY_LOADS, keyed by `gallery` itself (RFC 0005 §3a-FIX2 item 5), so a picker
   * instance other than the one that started the check - including a REPLACEMENT instance, after a
   * rerender - can still find and await the same result. With no decodable image at all, `onReady`
   * runs at once and nothing is tracked, because there is nothing left to wait for.
   *
   * Mirrors _showCardImage's own decode-rejection handling below: a rejection on an image that is
   * already `complete` is a TERMINAL result (loaded, or failed to load) - deciding on it immediately,
   * rather than also attaching load/error listeners, is what an earlier version of this got wrong: on
   * a genuinely broken image (complete with naturalWidth 0), the error event had already fired before
   * those listeners were attached, so neither ever ran and the gallery stayed "pending" forever. Only
   * an image that is NOT yet complete needs to wait for a future load/error event. */
  _watchGalleryImage(gallery, img, onReady, onFail) {
    if (!img || typeof img.decode !== 'function') {
      onReady();
      return;
    }
    GALLERY_LOADS.set(
      gallery,
      img.decode().then(onReady, () => {
        if (img.complete) {
          if (img.naturalWidth > 0) onReady();
          else onFail();
          return undefined;
        }
        return new Promise((resolve) => {
          img.addEventListener(
            'load',
            () => {
              onReady();
              resolve();
            },
            { once: true }
          );
          img.addEventListener(
            'error',
            () => {
              onFail();
              resolve();
            },
            { once: true }
          );
        });
      })
    );
  }

  /* Import `tpl` ONCE for `cardId`, insert it hidden directly after the page gallery, and wait for
   * its own first photo to actually load before ever showing it. Tracked with a
   * data-fall-gallery-state of pending -> ready or failed (RFC 0005 §3a-FIX1 item 5), the load itself
   * tracked in GALLERY_LOADS (RFC 0005 §3a-FIX2 item 5, see _watchGalleryImage) so a later
   * re-selection of the same card - a colour or quantity change while this is still pending, say, on
   * this instance or a replacement one - can await the SAME load in _showCardGallery instead of
   * starting a second import or revealing the gallery before it is ready.
   *
   * Forcing the initial image's loading attribute to eager mostly guards the FALLBACK query in
   * _initialGalleryImage, not the everyday case: product-gallery.liquid:135-139 already renders the
   * initial/default media with preload true, which media.liquid:46-58 turns into loading="eager" (or
   * no loading attribute at all, on a section placed past the third on the page - never "lazy"
   * either way), so an ordinary initial <img> would already load on its own even while hidden. The
   * fallback query only matters when the initial media is NOT an image at all (a video or a model) -
   * the first query then finds nothing, and the second one can land on a later, genuinely lazy cell.
   * [hidden] is display:none (theme.css), and a loading="lazy" image inside a display:none subtree
   * never loads - nothing ever reports it as "near the viewport" - so that cell needs the same
   * forced-eager treatment CARD IMAGE below already relies on for its own detached template img.
   * Every OTHER slide in the imported gallery is left exactly as Liquid rendered it: lazy, and picked
   * up by the theme's own inView preload once the gallery is actually shown, same as any other
   * carousel. */
  _importCardGallery(cardId, tpl, option, token) {
    const imported = document.importNode(tpl.content, true).querySelector('product-gallery');
    if (!imported) {
      this._hideAllCardGalleries();
      if (this.gallery) this.gallery.hidden = false;
      this._showCardImage(option);
      return;
    }
    this._ensureGalleryForm(imported.getAttribute('form'));
    imported.setAttribute('data-fall-gallery-card', cardId);
    imported.setAttribute('data-fall-gallery-owner', this.id);
    imported.setAttribute('data-fall-gallery-state', 'pending');
    imported.hidden = true;

    const carousel = imported.querySelector('.product-gallery__carousel');
    const initialImg = this._initialGalleryImage(carousel);
    if (initialImg) initialImg.loading = 'eager';

    if (this.gallery) this.gallery.after(imported);

    const succeed = () => {
      /* A rerender may have replaced this instance while the decode was still in flight; leave the
       * DOM alone rather than reveal or hide anything on behalf of a picker that is no longer on the
       * page (RFC 0005 §3a-FIX2 item 5) - whichever instance is current, if any, still finds this
       * same gallery by its owner id and card id the next time it is selected. */
      if (!this.isConnected) return;
      imported.setAttribute('data-fall-gallery-state', 'ready');
      if (token === this._galleryToken) this._revealCardGallery(imported, token);
    };
    const fail = () => {
      if (!this.isConnected) return;
      imported.setAttribute('data-fall-gallery-state', 'failed');
      if (token !== this._galleryToken) {
        /* RFC 0005 §3a-FIX3 item 1b: the shopper has already moved on to another card, so this
         * import no longer owns the selection - normally nothing to do, since a failed import is
         * never revealed in the first place. But if it is somehow still the gallery on screen (an
         * earlier reveal this same fail has otherwise raced), a broken photo must not be left
         * showing: hide it and fall back to the page gallery as the placeholder, same as every
         * other pending selection already shows while it waits. The pending card's own later
         * reveal hides the page gallery again once it lands, exactly as it already does today. */
        if (!imported.hidden) {
          imported.hidden = true;
          if (this.gallery) this.gallery.hidden = false;
        }
        return;
      }
      this._hideAllCardGalleries();
      if (this.gallery) this.gallery.hidden = false;
      this._showCardImage(option);
    };

    this._watchGalleryImage(imported, initialImg, succeed, fail);
  }

  /* The Liquid-default card's own gallery (RFC 0005 §3a-FIX2 item 6): the mover script in
   * fall-sale-picker.liquid already moved it into place and revealed it, during parsing, well before
   * this module could run - AC-11 needs the page to open on it with no flash of page photos, which
   * ruled out waiting for this deferred module to check it first. It marked the gallery
   * data-fall-gallery-state="unchecked" rather than "ready" for exactly that reason: put on screen,
   * not yet confirmed to actually show anything. This, run once on this card's first selection here,
   * is that same check, on the same initial image _importCardGallery watches for a freshly imported
   * gallery - broken switches back to the page's own photos immediately if this is still the active
   * selection, or, if the shopper has since selected another card that is still loading, switches to
   * the page gallery as a placeholder only when this gallery is the one still on screen (RFC 0005
   * §3a-FIX3 item 1b), so a broken default photo is never left as the visible fallback; and (via the
   * state it leaves behind) on every later visit too, the same as an imported gallery that failed;
   * still loading waits for it; already loaded is a same-tick no-op, since the gallery the shopper is
   * looking at right now is already the right one. */
  _checkDefaultGallery(gallery, option, token) {
    gallery.setAttribute('data-fall-gallery-state', 'pending');
    const carousel = gallery.querySelector('.product-gallery__carousel');
    const initialImg = this._initialGalleryImage(carousel);

    const succeed = () => {
      if (!this.isConnected) return;
      gallery.setAttribute('data-fall-gallery-state', 'ready');
      if (token === this._galleryToken) this._revealCardGallery(gallery, token);
    };
    const fail = () => {
      if (!this.isConnected) return;
      gallery.setAttribute('data-fall-gallery-state', 'failed');
      if (token !== this._galleryToken) {
        /* RFC 0005 §3a-FIX3 item 1b: the shopper has since selected another card, typically one
         * still importing, so the Liquid-default gallery the mover put on screen has been left
         * showing while this check was in flight. A broken photo must not be the placeholder while
         * the new card loads: hide it and fall back to the page gallery, the same placeholder every
         * other pending selection already shows. The pending card's own later reveal hides the page
         * gallery again once it lands, exactly as it already does today. */
        if (!gallery.hidden) {
          gallery.hidden = true;
          if (this.gallery) this.gallery.hidden = false;
        }
        return;
      }
      this._hideAllCardGalleries();
      if (this.gallery) this.gallery.hidden = false;
      this._showCardImage(option);
    };

    this._watchGalleryImage(gallery, initialImg, succeed, fail);
  }

  /* ---------------------------------------------------------------------------------------------
   * CARD IMAGE (#1). A card whose Liquid carries a template[data-fall-card-image] puts that photo
   * in the gallery's MAIN slide while it is selected; every other product photo stays where it was
   * and stays swipeable. A card with no image - the Single, say - shows the page's own photo.
   *
   * The swap is IN PLACE: the img element inside the main slide is exchanged and nothing else is
   * touched. No carousel cell is added, removed or hidden, so the theme's dots, counter, lightbox and
   * filter() indexes all stay valid - the carousel counts cells (theme.js `get cells`), the dots are
   * read once when the navigation connects, and the lightbox collects each cell's direct img child,
   * reading its width/height attributes. The previous _swapGalleryImage prepended a cell and hid
   * the rest with display:none, which the carousel does not treat as hidden, and its bare img had no
   * srcset, width or height. It never ran, because no card ever had an image.
   *
   * The image never reaches the form, Fly's state or the cart: the gallery DOES hold form controls -
   * its zoom, carousel-nav and thumbnail buttons are all <button> elements - but every one of them
   * sits outside every <form> on the page and carries no form attribute of its own, so none of them
   * can ever be submitted with the buy form regardless; a template's contents are invisible to every
   * selector the add path uses.
   * ------------------------------------------------------------------------------------------- */

  /* The gallery's main slide - the cell the page opens on, `.is-initial` (on MINA Pitcher the first
   * cell in the DOM is hidden, so it must be the unhidden initial cell, not the first child). Cached
   * once found. Null, and cached as null, when there is no gallery or the main slide is not an
   * image (a video, say): the feature is simply off. Null WITHOUT caching when the carousel has not
   * upgraded yet, so the caller can retry. */
  _cardImageSlot() {
    if (this._slot !== undefined) return this._slot;
    const carousel = this.gallery ? this.gallery.querySelector('.product-gallery__carousel') : null;
    if (!carousel) {
      this._slot = null;
      return null;
    }
    if (typeof carousel.select !== 'function' || !Array.isArray(carousel.cells)) return null;

    const slot =
      carousel.querySelector(':scope > .product-gallery__media.is-initial:not([hidden])') ||
      carousel.cells[0] ||
      null;
    const img = slot && slot.getAttribute('data-media-type') === 'image' ? slot.querySelector(':scope > img') : null;
    this._slot = img ? slot : null;
    /* The page's own img element. Put back AS IS for a no-image card, so it keeps its srcset and
     * fetchpriority and is never fetched again. */
    this._slotImg = img;
    this._slotCarousel = carousel;
    /* One imported img per card, reused on every later visit: the second visit is instant. */
    this._cardImgs = {};
    return this._slot;
  }

  _showCardImage(option) {
    const slot = this._cardImageSlot();
    if (!slot) {
      /* Not upgraded yet: retry ONCE when it is, with whatever is selected by then. Without this the
       * default card's image would never appear if the first _apply ran too early. */
      if (this._slot === undefined && !this._slotRetry) {
        this._slotRetry = true;
        customElements.whenDefined('scroll-carousel').then(() => {
          if (this.isConnected) this._showCardImage(this.selected);
        });
      }
      return;
    }

    const cardId = option ? option.dataset.fallOption || '' : '';
    /* A colour or quantity change re-applies the SAME card: nothing to do. */
    if (cardId === this._imageCardId) return;
    const initial = this._imageCardId === undefined;
    this._imageCardId = cardId;

    const tpl = option ? option.querySelector(':scope > template[data-fall-card-image]') : null;
    let next = this._slotImg;
    if (tpl) {
      /* importNode, NOT tpl.content.querySelector('img').cloneNode(true). A clone stays owned by the
       * template's inert document, which has no browsing context: it never fetches, and decode()
       * rejects at once, so the no-blank-frame wait below would silently do nothing. Imported into
       * this document, the img starts loading straight away, while still detached. */
      if (!this._cardImgs[cardId]) {
        const imported = document.importNode(tpl.content, true).querySelector('img');
        if (imported) this._cardImgs[cardId] = imported;
      }
      next = this._cardImgs[cardId] || this._slotImg;
    }

    const token = (this._imageToken = (this._imageToken || 0) + 1);
    const swap = (img) => {
      if (token !== this._imageToken) return; // a later selection won the race
      const current = slot.querySelector(':scope > img');
      if (!current || current === img) return; // no-image card to no-image card: nothing moves
      current.replaceWith(img);

      const carousel = this._slotCarousel;
      const index = carousel.cells.indexOf(slot);
      if (!initial && index > -1 && carousel.selectedCell !== slot) {
        /* The shopper may have swiped to another photo: bring them to the one that just changed.
         * Not on the initial apply - a shopper who swiped before the image decoded is left alone. */
        carousel.select(index);
      } else {
        /* Same slide, new picture. Let adaptive height re-measure it (theme.js listens for
         * carousel:settle) - a picture of a different shape would otherwise be clipped. */
        carousel.dispatchEvent(
          new CustomEvent('carousel:settle', { detail: { index: carousel.selectedIndex, cell: carousel.selectedCell } })
        );
      }
    };

    if (next === this._slotImg || typeof next.decode !== 'function') {
      swap(next);
      return;
    }
    /* Keep the current photo on screen until the new one is decoded: no blank frame. If it cannot
     * be shown - a failed fetch - the card falls back to the product's own photo rather than a
     * broken image. A rejection that arrives before the load has finished waits for the load. */
    const fallback = () => swap(next.naturalWidth > 0 ? next : this._slotImg);
    next.decode().then(
      () => swap(next),
      () => {
        if (next.complete) {
          fallback();
          return;
        }
        next.addEventListener('load', () => swap(next), { once: true });
        next.addEventListener('error', fallback, { once: true });
      }
    );
  }

  _findForm() {
    const id = this.dataset.formId;
    if (id) {
      const found = document.getElementById(id);
      if (found) return found;
    }
    return this.closest('form') || document.querySelector('form[action*="/cart/add"]');
  }

  /* Must never return a PARENT gallery (RFC 0005 §3a-FIX1 item 1, amended by §3a-FIX2 item 3 /
   * D-A285, D-A286). When this picker has its own data-form-id, the page gallery is whatever is bound
   * to that exact form and does not already carry data-fall-gallery-card - and NOTHING ELSE: with no
   * match, this returns null rather than falling back to a document-wide query, which used to be able
   * to return an unrelated product's gallery, or a parent gallery stranded between pickers, instead of
   * correctly reporting that this picker has no page gallery at all (RFC 0005 §5, "S2"). Only when
   * this picker carries no data-form-id at all - not expected on these templates, but the picker
   * element could in principle be used without one - does it fall back to any gallery in the document,
   * and even then never a parent gallery: excluded by data-fall-gallery-card (the attribute every
   * parent gallery is given the moment it enters the document, before it is ever shown) and, belt and
   * braces, by its bound form id ever starting with the inert `fall-gallery-form-` prefix every parent
   * gallery's own form uses (fall_gallery_form_id in fall-sale-picker.liquid), so it can never return a
   * parent gallery that happens not to carry the marker attribute yet either. */
  _findGallery() {
    const id = this.dataset.formId;
    if (id) {
      return document.querySelector('product-gallery[form="' + id + '"]:not([data-fall-gallery-card])');
    }
    return document.querySelector('product-gallery:not([data-fall-gallery-card]):not([form^="fall-gallery-form-"])');
  }
}

if (!customElements.get('fall-sale-picker')) {
  customElements.define('fall-sale-picker', FallSalePicker);
}
